// src/pages/Calculator.tsx
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { NumericStepper } from '../components';
import { AlertTriangle, BarChart3, Settings2, Maximize2, FileText } from 'lucide-react';
import { PdfViewerModal } from '../components/ui/PdfViewerModal';
import { SavedPdfReportsModal } from '../components/ui/SavedPdfReportsModal';
import { RecentCalculationsModal } from '../components/ui/RecentCalculationsModal';
import { savePdfReport, SavedPdfReport } from '../utils/pdfStorage';
import Sidebar from '../components/Sidebar';
import Checkbox from '../components/Checkbox';
import { apiPost, getUser, getJWT, supabase } from '../auth';
import { dataManager } from '../i18n/data';
import LoadingState from '../components/LoadingState';
import { appLogger } from '../utils/logger';
import { normalizeTakozCount } from '../utils/takoz';
import CogCard from '../components/cog';
import LashingCard, { LashingCardCaptureHandle } from '../components/lashing';
import { FloatingActionButton } from '../components/ui/FloatingActionButton';
import CustomDropdown from '../components/ui/CustomDropdown';
import ProjectModal from '../components/ui/ProjectModal';
import DimensionExceededDiagram from '../components/ui/DimensionExceededDiagram';

import CalculationWarningBanner from '../components/ui/CalculationWarningBanner';
import { enhancedDataManager } from '../utils/enhanced-data-manager';
import AppHeader from '../components/layout/AppHeader';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import LashingDiagramSVG from '../components/results/LashingDiagramSVG';
import { useDevice } from '../hooks/useDevice';
import { MobileNavBar, MobileTab } from '../components/layout/MobileNavBar';
import { hapticFeedback } from '../utils/nativeBridge';
import { saveCalculationOffline } from '../utils/offlineStorage';
import type {
    Language,
    SecuringElement,
    CargoDimensions,
    CenterOfGravity,
    CalculationResponse,
} from '../types';

export type { Language };

export type LashingPresence = {
    forward: { active: boolean; count: number; strength: number };
    backward: { active: boolean; count: number; strength: number };
    right: { active: boolean; count: number; strength: number };
    left: { active: boolean; count: number; strength: number };
};

type ManualLashingItem = {
    id: string;
    ropeKey: string;
    count: number;
    position?: 'above' | 'below';
    angle?: number;
};

export type ManualLashings = {
    forward: ManualLashingItem[];
    backward: ManualLashingItem[];
    right: ManualLashingItem[];
    left: ManualLashingItem[];
    loop: { ropeKey: string; count: number; position?: 'above' | 'below' };
};

const initialManualLashings: ManualLashings = {
    forward: [],
    backward: [],
    right: [],
    left: [],
    loop: { ropeKey: '', count: 0, position: 'below' }
};

const STORAGE_KEY = 'LASHING_CALCULATOR_STATE';

const loadState = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch (e) {
        console.error('Error loading state from localStorage:', e);
    }
    return null;
};


// =================== RESULTS TABLE ===================
const formatResultValue = (value: number): string => {
    if (Math.abs(value) < 1e-6) return '0';
    return value.toFixed(2);
};

interface ResultsTableProps {
    t: (key: string, namespace?: string) => string;
    data: Array<{
        direction: string;
        sliding: number;
        tipping: number;
        friction?: number;
        securing_element?: number;
        loop?: number;
        direct_lashing?: number;
        net_sliding: number;
        net_tipping: number;
        securing_element_type?: string;
    }>;
    response: CalculationResponse & { securing_element_type?: string };
    userEmail: string | null;
    userRole?: string;
    lashingDisplayMode?: 'msl' | 'cordstrap';
    woodenDunnageData?: SecuringElement;
}

const ResultsTable = ({ t, data, response, userEmail, userRole, lashingDisplayMode = 'msl', woodenDunnageData }: ResultsTableProps) => {
    const isFlatrack = ['flatrack_20', 'flatrack_40'].includes(response.transport_unit || '');
    // FIXED: Show warning if EITHER forward OR backward dunnage is 0 (not both)
    const hasForwardDunnage = (woodenDunnageData?.forces?.forward || 0) > 0;
    const hasBackwardDunnage = (woodenDunnageData?.forces?.backward || 0) > 0;
    const showDunnageWarning = isFlatrack && (!hasForwardDunnage || !hasBackwardDunnage);

    if (!data || !Array.isArray(data)) {
        return (
            <div className="text-center py-8 text-slate-400">
                <p>No results data available</p>
            </div>
        );
    }

    const isAdmin = userEmail?.endsWith('@admin.com') ?? false;
    const isAdvanceContext = ['advance', 'admin', 'surveyor'].includes(userRole || '') || isAdmin;

    if (!isAdvanceContext) {
        const sfHeader = t('results.safety_factor') === 'results.safety_factor' ? 'Safety Factor' : t('results.safety_factor');
        const statusHeader = t('results.status') === 'results.status' ? 'Status' : t('results.status');
        const passText = t('labels.pass') === 'labels.pass' ? 'Pass' : t('labels.pass');
        const failText = t('labels.fail') === 'labels.fail' ? 'Fail' : t('labels.fail');

        return (
            <div className="max-w-5xl mx-auto overflow-hidden rounded-xl border border-slate-300 dark:border-white/20 bg-white dark:bg-[#0D111C]/50 shadow-sm">
                <div className="px-4 py-2 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-[#161B2E]/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.5)]"></div>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">{t('force_analysis')}</h3>
                    </div>
                </div>

                {/* Mobile Cards View (< 768px) */}
                <div className="md:hidden space-y-3 p-2">
                    {data.map((row: any, i: number) => {
                        const resistingSliding = row.sliding - row.net_sliding;
                        const resistingTipping = row.tipping - row.net_tipping;
                        const sfSliding = row.sliding === 0 ? '∞' : (resistingSliding / row.sliding).toFixed(2);
                        const sfTipping = row.tipping === 0 ? '∞' : (resistingTipping / row.tipping).toFixed(2);
                        const slidingPass = row.net_sliding <= 0;
                        const tippingPass = row.net_tipping <= 0;
                        const isSafe = slidingPass && tippingPass;
                        const directionName = t(`app.calculator.direction.${row.direction.toLowerCase()}`);

                        return (
                            <div key={i} className="p-3.5 rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.03]">
                                <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-white/10">
                                    <h4 className="font-bold text-xs text-slate-900 dark:text-white uppercase tracking-wider">{directionName}</h4>
                                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                        isSafe ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'
                                    }`}>
                                        {isSafe ? passText : failText}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="p-2 rounded-xl bg-white dark:bg-slate-900/60 border border-slate-200/50 dark:border-white/5 flex flex-col gap-0.5">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase">{t('net_sliding')}</span>
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-slate-400">{sfHeader}:</span>
                                            <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{sfSliding}</span>
                                        </div>
                                    </div>
                                    <div className="p-2 rounded-xl bg-white dark:bg-slate-900/60 border border-slate-200/50 dark:border-white/5 flex flex-col gap-0.5">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase">{t('net_tipping')}</span>
                                        <div className="flex justify-between items-center text-[11px]">
                                            <span className="text-slate-400">{sfHeader}:</span>
                                            <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{sfTipping}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Desktop Table View (>= 768px) */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-center text-[13px] md:text-sm">
                        <thead className="text-[11px] md:text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/80 dark:bg-[#0D111C] !important">
                            <tr className="border-b border-slate-200 dark:border-white/10">
                                <th rowSpan={2} className="py-2 px-4 text-left font-bold text-slate-700 dark:text-slate-300">{t('direction')}</th>
                                <th colSpan={2} className="py-1 px-4 border-l border-slate-200 dark:border-white/10 text-blue-600 dark:text-sky-400">{t('net_sliding')}</th>
                                <th colSpan={2} className="py-1 px-4 border-l border-slate-200 dark:border-white/10 text-blue-600 dark:text-sky-400">{t('net_tipping')}</th>
                            </tr>
                            <tr className="border-b border-slate-200 dark:border-white/10 bg-slate-50/30 dark:bg-white/5">
                                <th className="py-1 px-3 border-l border-slate-200 dark:border-white/10 text-[10px] md:text-[11px] text-blue-600/80 dark:text-sky-400/80">{sfHeader}</th>
                                <th className="py-1 px-3 border-l border-slate-200 dark:border-white/10 text-[10px] md:text-[11px]">{statusHeader}</th>
                                <th className="py-1 px-3 border-l border-slate-200 dark:border-white/10 text-[10px] md:text-[11px] text-blue-600/80 dark:text-sky-400/80">{sfHeader}</th>
                                <th className="py-1 px-3 border-l border-slate-200 dark:border-white/10 text-[10px] md:text-[11px]">{statusHeader}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                            {data.map((row: any, i: number) => {
                                const resistingSliding = row.sliding - row.net_sliding;
                                const resistingTipping = row.tipping - row.net_tipping;
                                
                                const sfSliding = row.sliding === 0 ? '∞' : (resistingSliding / row.sliding).toFixed(2);
                                const sfTipping = row.tipping === 0 ? '∞' : (resistingTipping / row.tipping).toFixed(2);

                                const slidingPass = row.net_sliding <= 0;
                                const tippingPass = row.net_tipping <= 0;

                                return (
                                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="py-1.5 px-4 font-bold text-left text-slate-900 dark:text-white border-r border-slate-200 dark:border-white/10">
                                            {t(`app.calculator.direction.${row.direction.toLowerCase()}`)}
                                        </td>
                                        
                                        <td className="py-1.5 px-2 font-mono text-slate-600 dark:text-slate-400 bg-slate-50/30 dark:bg-transparent">{sfSliding}</td>
                                        <td className="py-1.5 px-3 border-l border-slate-200 dark:border-white/10">
                                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-tight ${slidingPass ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${slidingPass ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                                                {slidingPass ? passText : failText}
                                            </div>
                                        </td>

                                        <td className="py-1.5 px-2 font-mono text-slate-600 dark:text-slate-400 bg-slate-50/30 dark:bg-transparent border-l border-slate-200 dark:border-white/10">{sfTipping}</td>
                                        <td className="py-1.5 px-3 border-l border-slate-200 dark:border-white/10">
                                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-tight ${tippingPass ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${tippingPass ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                                                {tippingPass ? passText : failText}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {showDunnageWarning && (
                    <div className="mx-4 my-2 py-1.5 px-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/30 rounded-lg text-rose-700 dark:text-rose-300 font-bold text-[10px] flex items-center gap-3">
                        <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                        {t('results.flatrack_no_dunnage_warning')}
                    </div>
                )}
            </div>
        );
    }

    const resolveSecuringElementKey = () => {
        const typeFromResponse = response?.securing_element_type;
        const typeFromData = Array.isArray(data)
            ? data.find((row: any) => row?.securing_element_type)?.securing_element_type
            : null;

        const normalizedType = (typeFromResponse || typeFromData || 'wooden_dunnage').toLowerCase();
        return normalizedType === 'stopper' ? 'labels.stopper' : 'labels.wooden_dunnage';
    };

    const securingElementLabel = t(resolveSecuringElementKey());

    const headers = ['direction', 'sliding', 'tipping'];
    if (isAdmin) {
        headers.push('friction');
    }

    return (
        <div className="overflow-hidden">
            <div className="px-3 py-1.5 border-b border-slate-200/50 dark:border-white/10 bg-slate-50 dark:bg-[#161B2E]/30 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0 shadow-[0_0_8px_rgba(249,115,22,0.4)]"></div>
                <h3 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white leading-tight">{t('force_analysis')}</h3>
            </div>

            {/* Mobile Vertical Direction Cards (< 768px) */}
            <div className="md:hidden space-y-3 p-2">
                {data.map((row: any, i: number) => {
                    const isSlidingSafe = row.net_sliding <= 0;
                    const isTippingSafe = row.net_tipping <= 0;
                    const isSafe = isSlidingSafe && isTippingSafe;
                    const directionName = t(`app.calculator.direction.${row.direction.toLowerCase()}`);

                    return (
                        <div 
                            key={i} 
                            className={`p-3.5 rounded-2xl border transition-all shadow-sm ${
                                isSafe 
                                    ? 'bg-white dark:bg-[#0D111C]/90 border-slate-200 dark:border-white/10' 
                                    : 'bg-rose-50/60 dark:bg-rose-950/20 border-rose-300 dark:border-rose-500/30'
                            }`}
                        >
                            {/* Card Header: Direction Title & Safety Status Badge */}
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-2.5 mb-2.5">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.5)]"></div>
                                    <h4 className="font-bold text-xs sm:text-sm text-slate-900 dark:text-white uppercase tracking-wider">{directionName}</h4>
                                </div>
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                                    isSafe 
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-500/20' 
                                        : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 border border-rose-500/20'
                                }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${isSafe ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                    {isSafe ? (t('labels.pass') || 'GÜVENLİ') : (t('labels.fail') || 'YETERSİZ')}
                                </span>
                            </div>

                            {/* 2-Column Force Grid: Sliding vs Tipping */}
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                {/* Sliding Metric Box */}
                                <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/60 dark:border-white/5 flex flex-col justify-between gap-1">
                                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('sliding')}</span>
                                    <div className="flex justify-between items-baseline text-[11px]">
                                        <span className="text-slate-500">{t('sliding')}:</span>
                                        <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">{formatResultValue(row.sliding)} kN</span>
                                    </div>
                                    <div className="flex justify-between items-baseline text-[11px] pt-1 border-t border-slate-200/60 dark:border-white/5">
                                        <span className="text-slate-500 font-medium">{t('net_sliding')}:</span>
                                        <span className={`font-mono font-bold ${isSlidingSafe ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                            {formatResultValue(row.net_sliding)} kN
                                        </span>
                                    </div>
                                </div>

                                {/* Tipping Metric Box */}
                                <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/60 dark:border-white/5 flex flex-col justify-between gap-1">
                                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('tipping')}</span>
                                    <div className="flex justify-between items-baseline text-[11px]">
                                        <span className="text-slate-500">{t('tipping')}:</span>
                                        <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">{formatResultValue(row.tipping)} kN</span>
                                    </div>
                                    <div className="flex justify-between items-baseline text-[11px] pt-1 border-t border-slate-200/60 dark:border-white/5">
                                        <span className="text-slate-500 font-medium">{t('net_tipping')}:</span>
                                        <span className={`font-mono font-bold ${isTippingSafe ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                            {formatResultValue(row.net_tipping)} kN
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Securing Elements Breakdown Details */}
                            {isAdmin && (
                                <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-white/5 grid grid-cols-2 gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                                    <div className="flex justify-between px-1">
                                        <span>{t('friction')}:</span>
                                        <span className="font-mono text-slate-700 dark:text-slate-300 font-medium">{formatResultValue(row.friction)} kN</span>
                                    </div>
                                    <div className="flex justify-between px-1">
                                        <span>{securingElementLabel}:</span>
                                        <span className="font-mono text-slate-700 dark:text-slate-300 font-medium">{formatResultValue(row.securing_element)} kN</span>
                                    </div>
                                    <div className="flex justify-between px-1">
                                        <span>{t('looplash')}:</span>
                                        <span className="font-mono text-slate-700 dark:text-slate-300 font-medium">{formatResultValue(row.loop)} kN</span>
                                    </div>
                                    <div className="flex justify-between px-1">
                                        <span>{t('direct_lashing')}:</span>
                                        <span className="font-mono text-slate-700 dark:text-slate-300 font-medium">{formatResultValue(row.direct_lashing)} kN</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Desktop Table View (>= 768px) */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-[10px] md:text-sm text-right text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-white/10">
                    <thead className="text-[11px] md:text-xs font-bold uppercase tracking-widest text-orange-600 dark:text-orange-500">
                        <tr className="bg-slate-100 dark:bg-white/5 border-b border-slate-300 dark:border-white/10">
                            {headers.map(k => (
                                <th
                                    key={k}
                                    className={`py-1 px-3 ${k === 'direction' ? 'text-left' : 'text-center'} whitespace-normal leading-tight`}
                                >
                                    {t(k)}
                                </th>
                            ))}

                            {isAdmin ? (
                                <>
                                    <th className="py-1 px-3 text-center">{securingElementLabel}</th>
                                    {['looplash', 'direct_lashing'].map(k => (
                                        <th key={k} className="py-1 px-3 text-center">
                                            {lashingDisplayMode === 'cordstrap' 
                                                ? (k === 'looplash' ? 'CORDSTRAP LOOP' : 'CORDSTRAP DIRECT') 
                                                : t(k)}
                                        </th>
                                    ))}
                                </>
                            ) : (
                                <th className="py-1 px-3 text-center">{t('total_securing')}</th>
                            )}

                            {['net_sliding', 'net_tipping'].map(k => (
                                <th key={k} className="py-1 px-3 text-center">{t(k)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300 dark:divide-white/10">
                        {data.map((row: any, i: number) => {
                            const totalSecuring = (row.friction || 0) + (row.securing_element || 0) + (row.loop || 0) + (row.direct_lashing || 0);
                            const totalTippingSecuring = (row.friction || 0) + (row.securing_element || 0) + (row.loop_tipping || 0) + (row.direct_lashing_tipping || 0);
                            const cellClass = 'py-1 px-3 font-mono text-slate-900 dark:text-white text-center';
                            
                            const sLabel = t('sliding').charAt(0).toUpperCase();
                            const tLabel = t('tipping').charAt(0).toUpperCase();

                            return (
                                <tr key={i} className="hover:bg-slate-50/50 dark:hover:bg-white/5 transition-all duration-300">
                                    <td className="py-1 px-3 font-bold text-left text-orange-600 dark:text-orange-500 border-r border-slate-200 dark:border-white/10">
                                        {t(`app.calculator.direction.${row.direction.toLowerCase()}`)}
                                    </td>

                                    <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>{formatResultValue(row.sliding)}</td>
                                    <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>{formatResultValue(row.tipping)}</td>

                                    {isAdmin ? (
                                        <>
                                            <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>{formatResultValue(row.friction)}</td>
                                            <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>{formatResultValue(row.securing_element)}</td>
                                            <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>
                                                {row.loop_tipping !== undefined ? (
                                                    <div className="flex flex-col gap-0.5 max-w-[fit-content] mx-auto">
                                                        <div className="flex items-center justify-between gap-2 w-full">
                                                            <span className="text-[11px] whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">{sLabel}:</span>
                                                            <span className="text-[11px] whitespace-nowrap font-mono">{formatResultValue(row.loop)}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-2 w-full">
                                                            <span className="text-[11px] whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">{tLabel}:</span>
                                                            <span className="text-[11px] whitespace-nowrap font-mono">{formatResultValue(row.loop_tipping)}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    formatResultValue(row.loop)
                                                )}
                                            </td>
                                            <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>
                                                {row.direct_lashing_tipping !== undefined ? (
                                                    <div className="flex flex-col gap-0.5 max-w-[fit-content] mx-auto">
                                                        <div className="flex items-center justify-between gap-2 w-full">
                                                            <span className="text-[11px] whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">{sLabel}:</span>
                                                            <span className="text-[11px] whitespace-nowrap font-mono">{formatResultValue(row.direct_lashing)}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-2 w-full">
                                                            <span className="text-[11px] whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">{tLabel}:</span>
                                                            <span className="text-[11px] whitespace-nowrap font-mono">{formatResultValue(row.direct_lashing_tipping)}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    formatResultValue(row.direct_lashing)
                                                )}
                                            </td>
                                        </>
                                    ) : (
                                        <td className={`${cellClass} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>
                                            {(row.loop_tipping !== undefined || row.direct_lashing_tipping !== undefined) ? (
                                                <div className="flex flex-col gap-0.5 max-w-[fit-content] mx-auto">
                                                    <div className="flex items-center justify-between gap-2 w-full">
                                                        <span className="text-[11px] whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">{sLabel}:</span>
                                                        <span className="text-[11px] whitespace-nowrap font-mono">{formatResultValue(totalSecuring)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-2 w-full">
                                                        <span className="text-[11px] whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">{tLabel}:</span>
                                                        <span className="text-[11px] whitespace-nowrap font-mono">{formatResultValue(totalTippingSecuring)}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                formatResultValue(totalSecuring)
                                            )}
                                        </td>
                                    )}

                                    <td className={`py-1 px-3 font-bold text-center ${row.net_sliding > 0 ? 'text-rose-600' : 'text-emerald-600'} border-r border-slate-200 dark:border-white/10`} style={{ fontFeatureSettings: '"tnum"' }}>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md ${row.net_sliding > 0 ? 'bg-rose-100 dark:bg-rose-500/10 dark:shadow-[0_0_12px_rgba(244,63,94,0.1)]' : 'bg-emerald-100 dark:bg-emerald-500/10 dark:shadow-[0_0_12px_rgba(16,185,129,0.1)]'}`}>
                                            {row.net_sliding > 0 ? '✗' : '✓'}
                                            <span className="ml-1">{formatResultValue(row.net_sliding)}</span>
                                        </span>
                                    </td>
                                    <td className={`py-1 px-3 font-bold text-center ${row.net_tipping > 0 ? 'text-rose-600' : 'text-emerald-600'}`} style={{ fontFeatureSettings: '"tnum"' }}>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md ${row.net_tipping > 0 ? 'bg-rose-100 dark:bg-rose-500/10 dark:shadow-[0_0_12px_rgba(244,63,94,0.1)]' : 'bg-emerald-100 dark:bg-emerald-500/10 dark:shadow-[0_0_12px_rgba(16,185,129,0.1)]'}`}>
                                            {row.net_tipping > 0 ? '✗' : '✓'}
                                            <span className="ml-1">{formatResultValue(row.net_tipping)}</span>
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showDunnageWarning && (
                <div className="mt-2 py-2 px-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg text-rose-600 dark:text-rose-400 font-bold text-[10px] md:text-xs flex items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-500">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-pulse"></div>
                    {t('results.flatrack_no_dunnage_warning')}
                </div>
            )}
        </div>
    );
};

interface CalculatorProps {
    userEmail: string | null;
    onLogout: () => void;
}

const Calculator: React.FC<CalculatorProps> = ({ userEmail, onLogout }) => {
    const isAdmin = userEmail?.endsWith('@admin.com') ?? false;
    const { theme: currentTheme, setTheme } = useTheme();
    const uiTheme = ['modern', 'ocean', 'sunset', 'classic'].includes(currentTheme) ? 'dark' : 'light';
    const { t, lang, setLang } = useLanguage();
    const tr = (key: string, fallback: string) => {
        const val = t(key);
        return (!val || val === key || val.startsWith(key)) ? fallback : val;
    };
    const device = useDevice();
    const [mobileTab, setMobileTab] = useState<MobileTab>('inputs');
    const [mobileResultSubTab, setMobileResultSubTab] = useState<'table' | 'diagram'>('table');
    const [isFullscreenDiagramOpen, setIsFullscreenDiagramOpen] = useState(false);
    const [isPdfViewerModalOpen, setIsPdfViewerModalOpen] = useState(false);
    const [isSavedPdfModalOpen, setIsSavedPdfModalOpen] = useState(false);
    const [isRecentCalculationsModalOpen, setIsRecentCalculationsModalOpen] = useState(false);
    const [selectedPdfReport, setSelectedPdfReport] = useState<SavedPdfReport | null>(null);

    const handleThemeToggle = () => {
        setTheme(uiTheme === 'light' ? 'modern' : 'light-pro');
    };

    const [uiDataLoaded, setUiDataLoaded] = useState(false);
    const [transportModes, setTransportModes] = useState<{ value: string, label: string }[]>([]);
    const [materials, setMaterials] = useState<{ value: string, label: string }[]>([]);
    const [transportUnits, setTransportUnits] = useState<{ value: string, label: string, max_weight: number, con_strength: number, con_strength_loop: number, compatible_modes: string[], length: number, width: number, height: number }[]>([]);
    const [halatTypes, setHalatTypes] = useState<{ type: string, name: string, toka: string, cordstrap_name?: string, sbs: number, d_lashing_msl: number, l_lashing_msl: number, msl_tur: string, compatible_units: string[] }[]>([]);
    const [cappedDirectHalats, setCappedDirectHalats] = useState<{ type: string, name: string, toka: string, cordstrap_name?: string, sbs: number, d_lashing_msl: number, l_lashing_msl: number, msl_tur: string, compatible_units: string[] }[]>([]);
    const [dataError, setDataError] = useState<string | null>(null);
    const [termsAccepted, setTermsAccepted] = useState<boolean | null>(() => {
        try {
            return localStorage.getItem('terms_accepted') === 'true' ? true : null;
        } catch {
            return null;
        }
    });
    const [userRole, setUserRole] = useState<string>('basic');

    useEffect(() => {
        const checkTerms = async () => {
            const user = getUser();
            if (!user) return;

            // Ensure supabase session is authenticated before querying
            try {
                const { data: sessionData } = await supabase.auth.getSession();
                const token = getJWT();
                const refreshToken = localStorage.getItem('refresh_token');
                if (token && refreshToken && (!sessionData?.session || sessionData.session.access_token !== token)) {
                    await supabase.auth.setSession({ access_token: token, refresh_token: refreshToken });
                }
            } catch (e) {
                // Ignore session check errors
            }

            const { data, error } = await supabase
                .from('user_profiles')
                .select('terms_accepted, terms_accepted_at, lashing_display_mode, role, pdf_theme')
                .eq('id', user.sub || user.id)
                .maybeSingle();

            if (error) {
                appLogger.error('Error fetching user profile for terms check', error);
                // If there's an error, we don't set it to false immediately to avoid blocking user 
                // if it's just a temporary Supabase hiccup, but for safety we'll keep the current check.
            }

            if (data && (data.terms_accepted || data.terms_accepted_at)) {
                setTermsAccepted(true);
                try {
                    localStorage.setItem('terms_accepted', 'true');
                } catch {}
            } else {
                // Only explicitly set to false if we successfully got null data (record missing or terms not accepted)
                if (!error) {
                    setTermsAccepted(false);
                    try {
                        localStorage.removeItem('terms_accepted');
                    } catch {}
                }
            }

            const localLashingMode = localStorage.getItem('lashing_display_mode');
            const targetMode = (localLashingMode === 'msl' || localLashingMode === 'cordstrap')
                ? localLashingMode
                : (data?.lashing_display_mode || 'msl');

            if (targetMode === 'msl' || targetMode === 'cordstrap') {
                setLashingDisplayMode(targetMode);
                try {
                    localStorage.setItem('lashing_display_mode', targetMode);
                } catch {}
            }

            if (data && localLashingMode && data.lashing_display_mode !== localLashingMode && (localLashingMode === 'msl' || localLashingMode === 'cordstrap')) {
                const targetUserId = (user as any)?.sub || (user as any)?.id;
                if (targetUserId) {
                    supabase.from('user_profiles').update({
                        lashing_display_mode: localLashingMode,
                        updated_at: new Date().toISOString()
                    }).eq('id', targetUserId).then();
                }
            }

            const localPdfTheme = localStorage.getItem('pdf_theme');
            const targetTheme = (localPdfTheme === 'teal' || localPdfTheme === 'navy' || localPdfTheme === 'standart')
                ? localPdfTheme
                : (data?.pdf_theme || 'standart');

            if (targetTheme === 'teal' || targetTheme === 'navy' || targetTheme === 'standart') {
                setPdfTheme(targetTheme);
                try {
                    localStorage.setItem('pdf_theme', targetTheme);
                } catch {}
            }

            if (data && localPdfTheme && data.pdf_theme !== localPdfTheme && (localPdfTheme === 'teal' || localPdfTheme === 'navy' || localPdfTheme === 'standart')) {
                const targetUserId = (user as any)?.sub || (user as any)?.id;
                if (targetUserId) {
                    supabase.from('user_profiles').update({
                        pdf_theme: localPdfTheme,
                        updated_at: new Date().toISOString()
                    }).eq('id', targetUserId).then();
                }
            }

            if (data?.role) {
                setUserRole(data.role);
            }
        };
        checkTerms();
    }, [userEmail, supabase]); // Re-run if supabase client or user email changes

    useEffect(() => {
        let isMounted = true;
        const loadData = async () => {
            try {
                appLogger.info('Starting to load UI data');
                if (!dataManager) {
                    appLogger.error('Data manager not available', new Error('Data manager is not available'));
                    throw new Error('Data manager is not available');
                }
                await dataManager.load();
                if (!isMounted) return;
                const modes = dataManager.getTransportModes();
                const mats = dataManager.getMaterials();
                const units = dataManager.getTransportUnits();
                const halats = dataManager.getHalatTypes();
                appLogger.info('Data loaded successfully', { modesCount: modes.length, matsCount: mats.length, unitsCount: units.length, halatsCount: halats.length });
                if (modes.length === 0 || mats.length === 0 || units.length === 0) {
                    throw new Error('Incomplete data loaded from server');
                }
                setTransportModes(modes);
                setMaterials(mats);
                setTransportUnits(units);
                setHalatTypes(halats);
                setDataError(null);
                if (isMounted) {
                    setUiDataLoaded(true);
                }
            } catch (error: unknown) {
                appLogger.error('Critical error loading data', error as Error);
                if (isMounted) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                    setDataError(`Failed to load application data: ${errorMessage}`);
                    setTransportModes([]);
                    setMaterials([]);
                    setTransportUnits([]);
                    setHalatTypes([]);
                    setUiDataLoaded(false);
                }
            }
        };
        loadData();
        return () => {
            isMounted = false;
        };
    }, []);

    const [persistedState] = useState(() => loadState());
    const [lashingDisplayMode, setLashingDisplayMode] = useState<'msl' | 'cordstrap'>(() => {
        const directMode = localStorage.getItem('lashing_display_mode');
        if (directMode === 'msl' || directMode === 'cordstrap') return directMode;
        return persistedState?.lashingDisplayMode ?? 'msl';
    });
    const [pdfTheme, setPdfTheme] = useState<'teal' | 'navy' | 'standart'>(() => {
        const directTheme = localStorage.getItem('pdf_theme');
        if (directTheme === 'teal' || directTheme === 'navy' || directTheme === 'standart') return directTheme;
        return persistedState?.pdfTheme ?? 'teal';
    });

    useEffect(() => {
        const handleLashingModeUpdate = (e?: any) => {
            const newMode = e?.detail || localStorage.getItem('lashing_display_mode');
            if (newMode === 'msl' || newMode === 'cordstrap') {
                setLashingDisplayMode(newMode);
            }
        };
        const handlePdfThemeUpdate = (e?: any) => {
            const newTheme = e?.detail || localStorage.getItem('pdf_theme');
            if (newTheme === 'teal' || newTheme === 'navy' || newTheme === 'standart') {
                setPdfTheme(newTheme);
            }
        };
        const handleStorage = (e: StorageEvent) => {
            if (e.key === 'lashing_display_mode') handleLashingModeUpdate();
            if (e.key === 'pdf_theme') handlePdfThemeUpdate();
        };

        window.addEventListener('lashing_mode_changed', handleLashingModeUpdate);
        window.addEventListener('pdf_theme_changed', handlePdfThemeUpdate);
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener('lashing_mode_changed', handleLashingModeUpdate);
            window.removeEventListener('pdf_theme_changed', handlePdfThemeUpdate);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    const [weight, setWeight] = useState(persistedState?.weight ?? 0);
    const [calculationMode, setCalculationMode] = useState<'optimizer' | 'calculator'>(persistedState?.calculationMode ?? 'optimizer');
    const [manualLashings, setManualLashings] = useState<ManualLashings>(persistedState?.manualLashings ?? initialManualLashings);
    const prevUnitRef = useRef('');
    const prevModeRef = useRef<'optimizer' | 'calculator'>('optimizer');
    const isEditingOptimizerResult = useRef(false);
    const [transportMode, setTransportMode] = useState(persistedState?.transportMode ?? '');
    const [transportUnit, setTransportUnit] = useState(persistedState?.transportUnit ?? '');
    const [frictionSurface, setFrictionSurface] = useState(persistedState?.frictionSurface ?? '');
    const [dimensions, setDimensions] = useState<CargoDimensions>(persistedState?.dimensions ?? { width: 0, length: 0, height: 0 });
    const [cog, setCog] = useState<CenterOfGravity>(persistedState?.cog ?? { width: 0.1, length: 0.1, height: 0.3 });
    const [cogDisplay, setCogDisplay] = useState<Record<keyof CenterOfGravity, string>>(persistedState?.cogDisplay ?? { width: '0.1', length: '0.1', height: '0.3' });
    const [woodenDunnageEnabled, setWoodenDunnageEnabled] = useState(persistedState?.woodenDunnageEnabled ?? false);
    const [woodenDunnageData, setWoodenDunnageData] = useState<SecuringElement>(persistedState?.woodenDunnageData ?? {
        width: 0,
        length: 0,
        height: 0,
        qty: 0,
        forces: { forward: 0, backward: 0, right: 0, left: 0 },
    });
    const [stopperEnabled, setStopperEnabled] = useState(persistedState?.stopperEnabled ?? false);
    const [stopperData, setStopperData] = useState<Pick<SecuringElement, 'forces'>>(persistedState?.stopperData ?? {
        forces: { forward: 0, backward: 0, right: 0, left: 0 },
    });
    const [loopLashingEnabled, setLoopLashingEnabled] = useState(persistedState?.loopLashingEnabled ?? false);
    const [selectedLoopRope, setSelectedLoopRope] = useState<string | null>(persistedState?.selectedLoopRope ?? null);
    const [selectedDirectRopes, setSelectedDirectRopes] = useState<Record<string, boolean>>(persistedState?.selectedDirectRopes ?? {});
    const [breakBulkFlatrackType, setBreakBulkFlatrackType] = useState<'flatrack_20' | 'flatrack_40'>(persistedState?.breakBulkFlatrackType ?? 'flatrack_20');
    const [breakBulkOrientation, setBreakBulkOrientation] = useState<'longitudinal' | 'transverse'>(persistedState?.breakBulkOrientation ?? 'longitudinal');
    const [breakBulkFlatrackCount, setBreakBulkFlatrackCount] = useState<number>(persistedState?.breakBulkFlatrackCount ?? 2);
    const [breakBulkFlatrackGap, setBreakBulkFlatrackGap] = useState<number>(persistedState?.breakBulkFlatrackGap ?? 0.15);
    const [projectDescription, setProjectDescription] = useState(persistedState?.projectDescription ?? '');
    const [optimizerResultsTransferred, setOptimizerResultsTransferred] = useState(false);

    const selectedTransportUnitData = useMemo(() => {
        if (!transportUnit) return null;
        return transportUnits.find(u => u.value === transportUnit) || null;
    }, [transportUnits, transportUnit]);

    const isOverweight = useMemo(() => {
        if (!selectedTransportUnitData) return false;
        const maxWeight = selectedTransportUnitData.max_weight;
        if (!maxWeight || maxWeight <= 0) return false;
        return weight > maxWeight;
    }, [selectedTransportUnitData, weight]);

    // Determine if cargo width exceeds unit width for specific units (Flatrack, Lowbed, Mafi)
    const isOversizedWidth = useMemo(() => {
        if (!transportUnit || !selectedTransportUnitData) return false;
        
        // Sadece Flatrack, Lowbed ve Mafi tipleri için taşma kontrolü (Kural: Breakbulk hariç)
        const isGroupB = ['flatrack_20', 'flatrack_40', 'lowbed', 'mafi'].includes(transportUnit);
        
        if (isGroupB && selectedTransportUnitData.width > 0) {
            return Number(dimensions.width) > selectedTransportUnitData.width;
        }
        return false;
    }, [transportUnit, selectedTransportUnitData, dimensions.width]);

    const loopHalatOptions = useMemo(() => {
        if (!transportUnit) return [];
        const connectionStrength = selectedTransportUnitData?.con_strength ?? null;
        const connectionStrengthLoop = selectedTransportUnitData?.con_strength_loop ?? connectionStrength;

        return (cappedDirectHalats.length > 0 ? cappedDirectHalats : halatTypes || [])
            .filter(h => h.l_lashing_msl > 0 && h.compatible_units.includes(transportUnit))
            .map(h => {
                const cappedLoopMSL = connectionStrengthLoop !== null ? Math.min(h.l_lashing_msl, connectionStrengthLoop) : h.l_lashing_msl;
                const cappedDirectMSL = connectionStrength !== null ? Math.min(h.d_lashing_msl, connectionStrength) : h.d_lashing_msl;
                return {
                    ...h,
                    cappedLoopMSL,
                    cappedDirectMSL,
                    optionKey: `${h.name}-${h.toka}`
                };
            });
    }, [cappedDirectHalats, halatTypes, transportUnit, selectedTransportUnitData, lashingDisplayMode]);

    const sortedLoopHalatOptions = useMemo(() => {
        return [...loopHalatOptions].sort((a, b) => a.cappedLoopMSL - b.cappedLoopMSL);
    }, [loopHalatOptions]);


    useEffect(() => {
        const currentMax = breakBulkOrientation === 'transverse' ? dimensions.length : dimensions.width;
        const boundedMax = Number((currentMax * 0.9).toFixed(2));
        if (breakBulkFlatrackGap > boundedMax) {
            setBreakBulkFlatrackGap(Math.max(0.15, boundedMax));
        }
    }, [dimensions.length, dimensions.width, breakBulkOrientation, breakBulkFlatrackGap]);

    // === BREAKBULK: Auto-compute suggested Flatrack count from cargo dimensions ===
    const breakbulkSuggested = useMemo(() => {
        if (transportUnit !== 'breakbulk') return null;
        const FLATRACK_STEP = 2.44 + breakBulkFlatrackGap;
        const FLATRACK_OFFSET = breakBulkFlatrackGap;
        const FLATRACK_LENGTHS: Record<string, number> = { flatrack_20: 5.61, flatrack_40: 11.65 };
        const flatrackLen = FLATRACK_LENGTHS[breakBulkFlatrackType] ?? 11.65;
        const cargoWidth = Number(dimensions.width) || 0;
        const cargoLength = Number(dimensions.length) || 0;

        let dimensionForCount: number;
        let warning: string | null = null;

        if (breakBulkOrientation === 'transverse') {
            dimensionForCount = cargoLength;
            // Spec: Transverse -> Cargo width < flatrack type LENGTH
            if (cargoWidth >= flatrackLen) {
                warning = t('errors.breakbulk_dim_overflow_width')
                    .replace('{cargo_width}', cargoWidth.toFixed(2))
                    .replace('{flatrack_length}', flatrackLen.toFixed(2));
            }
        } else {
            dimensionForCount = cargoWidth;
            // Spec: Longitudinal -> Cargo length < flatrack type LENGTH
            if (cargoLength >= flatrackLen) {
                warning = t('errors.breakbulk_dim_overflow_length')
                    .replace('{cargo_length}', cargoLength.toFixed(2))
                    .replace('{flatrack_length}', flatrackLen.toFixed(2));
            }
        }

        const recommendedCount = Math.max(2, Math.ceil((dimensionForCount + FLATRACK_OFFSET) / FLATRACK_STEP));
        const lCount = Math.ceil(breakBulkFlatrackCount / 2);
        const rCount = breakBulkFlatrackCount - lCount;

        return {
            recommendedCount,
            left: lCount,
            right: rCount,
            warning
        };
    }, [transportUnit, breakBulkOrientation, breakBulkFlatrackType, breakBulkFlatrackCount, breakBulkFlatrackGap, dimensions.width, dimensions.length, t]);

    // === BREAKBULK: Auto-reset count on type/orientation change ===
    useEffect(() => {
        if (transportUnit === 'breakbulk' && breakbulkSuggested) {
            setBreakBulkFlatrackCount(breakbulkSuggested.recommendedCount);
        }
    }, [breakBulkFlatrackType, breakBulkOrientation]);

    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [results, setResults] = useState<CalculationResponse | null>(null);
    const isVesselSelected = transportUnit === 'vessel'; // Logic updated to match stopper availability
    const [error, setError] = useState<string | null>(null);
    const [exceededDimsInfo, setExceededDimsInfo] = useState<{ dims: ('width' | 'length' | 'height')[]; unitName: string } | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [overweightPending, setOverweightPending] = useState(false);
    const [overweightWarning, setOverweightWarning] = useState<string | null>(null);
    const [show3DView, setShow3DView] = useState(false);
    const [show3DLashingDiagram, setShow3DLashingDiagram] = useState(false);
    const enable3DLashingView = true;
    const lashingCardRef = useRef<LashingCardCaptureHandle>(null);

    // Persistence Effect
    useEffect(() => {
        const stateToSave = {
            weight,
            calculationMode,
            manualLashings,
            transportMode,
            transportUnit,
            frictionSurface,
            dimensions,
            cog,
            cogDisplay,
            woodenDunnageEnabled,
            woodenDunnageData,
            stopperEnabled,
            stopperData,
            loopLashingEnabled,
            selectedLoopRope,
            selectedDirectRopes,
            breakBulkFlatrackType,
            breakBulkOrientation,
            breakBulkFlatrackOrientation: breakBulkOrientation, // (Legacy/Unused but keeping matching pattern if it existed)
            breakBulkFlatrackCount,
            breakBulkFlatrackGap,
            projectDescription,
            lashingDisplayMode,
            pdfTheme
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
            if (lashingDisplayMode) localStorage.setItem('lashing_display_mode', lashingDisplayMode);
            if (pdfTheme) localStorage.setItem('pdf_theme', pdfTheme);
        } catch (storageErr) {
            console.warn('Could not persist calculator state to localStorage:', storageErr);
        }
    }, [
        weight, calculationMode, manualLashings, transportMode,
        transportUnit, frictionSurface, dimensions, cog,
        cogDisplay, woodenDunnageEnabled, woodenDunnageData,
        stopperEnabled, stopperData, loopLashingEnabled,
        selectedLoopRope, selectedDirectRopes,
        breakBulkFlatrackType, breakBulkOrientation, breakBulkFlatrackCount, breakBulkFlatrackGap,
        projectDescription, lashingDisplayMode, pdfTheme
    ]);

    // Reset results when language changes to avoid stale translations
    useEffect(() => {
        setResults(null);
        setError(null);
    }, [lang]);

    const [flatrackWarningPending, setFlatrackWarningPending] = useState(false);

    const [showFlatrackWarning, setShowFlatrackWarning] = useState(false);



    useEffect(() => {
        if (!enable3DLashingView && show3DLashingDiagram) {
            setShow3DLashingDiagram(false);
        }
    }, [enable3DLashingView, show3DLashingDiagram]);

    const resetWoodenDunnageData = useCallback(() => {
        setWoodenDunnageData({
            width: 0,
            length: 0,
            height: 0,
            qty: 0,
            forces: { forward: 0, backward: 0, right: 0, left: 0 }
        });
    }, []);

    const resetStopperData = useCallback(() => {
        setStopperData({
            forces: { forward: 0, backward: 0, right: 0, left: 0 }
        });
    }, []);

    const handleCalculationReset = useCallback(() => {
        setResults(null);
        setError(null);
        setOptimizerResultsTransferred(false);
    }, []);

    const handleLoopLashingToggle = useCallback((enabled: boolean) => {
        setLoopLashingEnabled(enabled);
        
        // Reset rope selection when enabling/disabling - no default selection
        if (!enabled) {
            setSelectedLoopRope(null);
        }
        
        setResults(null);
        setError(null);
    }, [setResults, setError, setManualLashings]);

    const handleWoodenDunnageToggle = useCallback((enabled: boolean) => {
        if (enabled && isVesselSelected) return;
        setWoodenDunnageEnabled(enabled);
        setError(null); // Clear error on toggle
        handleCalculationReset();

        if (!enabled) {
            resetWoodenDunnageData();
            return;
        }
        if (stopperEnabled) {
            setStopperEnabled(false);
            resetStopperData();
        }
    }, [handleCalculationReset, isVesselSelected, resetStopperData, resetWoodenDunnageData, stopperEnabled]);

    const handleStopperToggle = useCallback((enabled: boolean) => {
        if (!isVesselSelected) return;
        setStopperEnabled(enabled);
        setError(null); // Clear error on toggle
        if (enabled) {
            setStopperData({ forces: { forward: 0, backward: 0, right: 0, left: 0 } });
            handleCalculationReset();
        }
        if (!enabled) {
            resetStopperData();
            handleCalculationReset();
            return;
        }
        if (woodenDunnageEnabled) {
            setWoodenDunnageEnabled(false);
            resetWoodenDunnageData();
            handleCalculationReset();
        }
    }, [handleCalculationReset, isVesselSelected, resetStopperData, resetWoodenDunnageData, woodenDunnageEnabled]);

    useEffect(() => {
        if (!isVesselSelected && stopperEnabled) {
            setStopperEnabled(false);
            resetStopperData();
        }
    }, [isVesselSelected, resetStopperData, stopperEnabled]);

    useEffect(() => {
        if (isVesselSelected && woodenDunnageEnabled) {
            setWoodenDunnageEnabled(false);
            resetWoodenDunnageData();
        }
    }, [isVesselSelected, resetWoodenDunnageData, woodenDunnageEnabled]);

    const validUnits = useMemo(() => {
        if (!transportMode) return [];
        const normalizedMode = transportMode.toLowerCase().trim();
        return transportUnits.filter(u => Array.isArray(u.compatible_modes)
            && u.compatible_modes.some(m => (m || '').toLowerCase().trim() === normalizedMode)
        );
    }, [transportUnits, transportMode]);

    // CRITICAL: Reset transportUnit if it's no longer valid for the selected mode
    useEffect(() => {
        if (transportUnit && transportMode && uiDataLoaded) {
            const isCompatible = validUnits.some(u => u.value === transportUnit);
            if (!isCompatible) {
                appLogger.info('Resetting transport unit as it is incompatible with the new mode', { transportMode, transportUnit });
                setTransportUnit('');
                setResults(null);
            }
        }
    }, [transportMode, validUnits, transportUnit, uiDataLoaded]);



    useEffect(() => {
        if (transportUnit && uiDataLoaded) {
            const fetchCappedHalats = async () => {
                try {
                    const cappedHalats = await enhancedDataManager.findDirectLashingHalatlar(transportUnit, isOversizedWidth);
                    setCappedDirectHalats(cappedHalats);
                } catch (error) {
                    console.error('Failed to fetch capped halats:', error);
                    setCappedDirectHalats(halatTypes.filter(h => h.d_lashing_msl > 0 && h.compatible_units.includes(transportUnit)));
                }
            };
            fetchCappedHalats();
        }
    }, [transportUnit, uiDataLoaded, halatTypes, dimensions.width, selectedTransportUnitData, isOversizedWidth]);

    useEffect(() => {
        if (isEditingOptimizerResult.current) {
            isEditingOptimizerResult.current = false;
            return;
        }

        const availableHalats = cappedDirectHalats.length > 0 ? cappedDirectHalats : halatTypes || [];
        const defaultSelectedRopes: Record<string, boolean> = {};
        availableHalats
            .filter(h => h.d_lashing_msl > 0 && h.compatible_units.includes(transportUnit))
            .forEach(h => {
                defaultSelectedRopes[`${h.name}-${h.toka}`] = true;
            });
        setSelectedDirectRopes(defaultSelectedRopes);
        setSelectedLoopRope(null);
    }, [transportUnit, cappedDirectHalats, halatTypes]);

    // Separate effect to handle manual lashings reset ONLY on unit or mode change
    useEffect(() => {
        if (calculationMode === 'calculator') {
            // Only reset if we are NOT coming from optimizer edit result
            if (!isEditingOptimizerResult.current) {
                setManualLashings({
                    forward: [{ id: '1', ropeKey: '', count: 0, position: 'above' }],
                    backward: [{ id: '1', ropeKey: '', count: 0, position: 'above' }],
                    right: [{ id: '1', ropeKey: '', count: 0, position: 'above', angle: 45 }],
                    left: [{ id: '1', ropeKey: '', count: 0, position: 'above', angle: 45 }],
                    loop: { ropeKey: '', count: 0 },
                });
            }
        } else {
            setManualLashings(initialManualLashings);
        }
    }, [transportUnit]);

    useEffect(() => {
        const halfWidth = Math.max(0.1, dimensions.width / 2);
        const halfLength = Math.max(0.1, dimensions.length / 2);
        const halfHeight = Math.max(0.1, dimensions.height / 2);
        setCog({
            width: halfWidth,
            length: halfLength,
            height: halfHeight,
        });
        setCogDisplay({
            width: formatDecimal(halfWidth),
            length: formatDecimal(halfLength),
            height: formatDecimal(halfHeight),
        });
    }, [dimensions]);

    const formatDecimal = (value: number): string => {
        const str = value.toString();
        if (str.includes('.')) {
            return str.replace(/\.?0+$/, '').replace(/\.$/, '');
        }
        return str;
    };

    const handleDimensionChange = useCallback((field: keyof CargoDimensions, value: string) => {
        let numValue = parseFloat(value) || 0;
        let adjustedDimensions = false;

        setDimensions(prev => {
            let newWidth = prev.width;
            let newLength = prev.length;
            let newHeight = prev.height;

            if (field === 'width') {
                newWidth = numValue;
                if (newWidth > prev.length) {
                    newWidth = prev.length;
                    adjustedDimensions = true;
                }
            } else if (field === 'length') {
                newLength = numValue;
                if (newLength < prev.width) {
                    newWidth = newLength;
                    adjustedDimensions = true;
                }
            } else if (field === 'height') {
                newHeight = numValue;
            }

            const newDimensions = { width: newWidth, length: newLength, height: newHeight };
            let newCog = { ...cog };
            let newCogDisplay = { ...cogDisplay };
            let hasCogError = false;

            if (newCog.width > newDimensions.width * 0.9) {
                const adjustedValue = Math.max(0.1, newDimensions.width * 0.9);
                newCog.width = adjustedValue;
                newCogDisplay.width = formatDecimal(adjustedValue);
                hasCogError = true;
            }
            if (newCog.length > newDimensions.length * 0.9) {
                const adjustedValue = Math.max(0.1, newDimensions.length * 0.9);
                newCog.length = adjustedValue;
                newCogDisplay.length = formatDecimal(adjustedValue);
                hasCogError = true;
            }
            if (newCog.height > newDimensions.height * 0.9) {
                const adjustedValue = Math.max(0.1, newDimensions.height * 0.9);
                newCog.height = adjustedValue;
                newCogDisplay.height = formatDecimal(adjustedValue);
                hasCogError = true;
            }

            if (hasCogError) {
                setCog(newCog);
                setCogDisplay(newCogDisplay);
            }

            if (adjustedDimensions) {
                setError(t('errors.cargo_width_greater_than_length'));
            }

            return newDimensions;
        });
        setResults(null);
        if (!adjustedDimensions) {
            setError(null);
        }
    }, [cog, cogDisplay, formatDecimal, t]);

    const handleCogChange = useCallback((field: keyof CenterOfGravity, value: string) => {
        setCogDisplay(prev => ({ ...prev, [field]: value }));
        const numValue = parseFloat(value) || 0;
        setCog(prev => ({ ...prev, [field]: numValue }));
        setResults(null);
        setError(null);
    }, [dimensions]);


    // Auto-enable wooden dunnage for Flatrack in Optimizer mode (suggestion only)
    useEffect(() => {
        const isFlatrack = transportUnit === 'flatrack_20' || transportUnit === 'flatrack_40';
        const wasFlatrack = prevUnitRef.current === 'flatrack_20' || prevUnitRef.current === 'flatrack_40';
        const unitChanged = prevUnitRef.current !== transportUnit;
        const modeChanged = prevModeRef.current !== calculationMode;

        // Only auto-enable when unit or mode changes, not continuously
        if (calculationMode === 'optimizer' && isFlatrack && (unitChanged || modeChanged)) {
            if (!woodenDunnageEnabled) {
                setWoodenDunnageEnabled(true);
            }

            // Set default dimensions and quantity for Flatrack dunnage
            setWoodenDunnageData(prev => ({
                ...prev,
                width: 10,
                length: 10,
                forces: {
                    ...prev.forces,
                    forward: 2,
                    backward: 2
                }
            }));
        }
        // Reset dunnage when moving away from Flatrack to avoid stale specialized settings
        else if (unitChanged && wasFlatrack && !isFlatrack) {
            setWoodenDunnageEnabled(false);
            resetWoodenDunnageData();
            setResults(null);
        }

        prevUnitRef.current = transportUnit;
        prevModeRef.current = calculationMode;
    }, [transportUnit, calculationMode, woodenDunnageEnabled, resetWoodenDunnageData]);

    // Reset Flatrack warning state when unit or dunnage toggle changes
    useEffect(() => {
        setFlatrackWarningPending(false);
    }, [transportUnit, woodenDunnageEnabled]);

    // Synchronize manual loop rope key with dropdown selection
    useEffect(() => {
        if (selectedLoopRope && calculationMode === 'calculator') {
            setManualLashings(prev => {
                if (prev.loop.ropeKey === selectedLoopRope) return prev;
                return {
                    ...prev,
                    loop: { ...prev.loop, ropeKey: selectedLoopRope }
                };
            });
        }
    }, [selectedLoopRope, calculationMode]);

    // Note: Loop lashing rope selection is now required to be manually selected by user
    // No automatic default selection - user must explicitly choose a rope






    const handleCalculationModeChange = (mode: 'optimizer' | 'calculator') => {
        setCalculationMode(mode);
        setResults(null);
        setError(null);
        setOptimizerResultsTransferred(false);
        if (mode === 'optimizer') {
            setManualLashings(initialManualLashings);
        } else {
            setResults(null);
            
            // Get a default rope key for loop lashing if enabled
            let defaultLoopKey = '';
            if (loopLashingEnabled && sortedLoopHalatOptions.length > 0) {
                // Use currently selected optimizer rope if available, otherwise first option
                defaultLoopKey = selectedLoopRope || sortedLoopHalatOptions[0].optionKey;
            }

            setManualLashings({
                forward: [{ id: '1', ropeKey: '', count: 0, position: 'above' }],
                backward: [{ id: '1', ropeKey: '', count: 0, position: 'above' }],
                right: [{ id: '1', ropeKey: '', count: 0, position: 'above', angle: 45 }],
                left: [{ id: '1', ropeKey: '', count: 0, position: 'above', angle: 45 }],
                loop: { ropeKey: defaultLoopKey, count: 0 },
            });
        }
        if (transportUnit) {
            const availableHalats = cappedDirectHalats.length > 0 ? cappedDirectHalats : halatTypes || [];
            const defaultSelectedRopes: Record<string, boolean> = {};
            availableHalats
                .filter(h => h.d_lashing_msl > 0 && h.compatible_units.includes(transportUnit))
                .forEach(h => {
                    defaultSelectedRopes[`${h.name}-${h.toka}`] = true;
                });
            setSelectedDirectRopes(defaultSelectedRopes);
        }
        // Removed: setSelectedLoopRope(null); -- This was wiping state when switching to manual mode
    };

    const normalizeStopperCount = (next: number, prev: number) => {
        if (next <= 0) return 0;
        if (prev >= 2 && next < 2) return 0;
        return next < 2 ? 2 : next;
    };

    const prepareCalculationData = useCallback(() => {
        const availableHalats = cappedDirectHalats.length > 0 ? cappedDirectHalats : halatTypes || [];
        const compatibleDirectRopes = availableHalats.filter(h => h.d_lashing_msl > 0 && h.compatible_units.includes(transportUnit));
        let effectiveSelectedDirectRopes = selectedDirectRopes;
        const hasDirectSelectionForCurrentUnit = compatibleDirectRopes.some((h) => selectedDirectRopes[`${h.name}-${h.toka}`]);
        if (!hasDirectSelectionForCurrentUnit && compatibleDirectRopes.length > 0) {
            const fallbackSelection = compatibleDirectRopes.reduce((acc, rope) => {
                acc[`${rope.name}-${rope.toka}`] = true;
                return acc;
            }, {} as Record<string, boolean>);
            effectiveSelectedDirectRopes = fallbackSelection;
            setSelectedDirectRopes(fallbackSelection);
        }

        const manualLoopRopeKey = manualLashings.loop.ropeKey || null;
        const manualLoopCount = Number(manualLashings.loop.count) || 0;
        const shouldUseManualLoop = (calculationMode === 'calculator' && loopLashingEnabled && !!manualLoopRopeKey && manualLoopCount > 0);
        
        let effectiveSelectedLoopRope: string | null = null;
        if (calculationMode === 'calculator') {
            effectiveSelectedLoopRope = shouldUseManualLoop ? manualLoopRopeKey : null;
        } else {
            effectiveSelectedLoopRope = selectedLoopRope;
            if (loopLashingEnabled && !effectiveSelectedLoopRope && sortedLoopHalatOptions.length > 0) {
                effectiveSelectedLoopRope = sortedLoopHalatOptions[0].optionKey;
            }
        }
        
        const loopLashingFlag = calculationMode === 'calculator' ? shouldUseManualLoop : loopLashingEnabled;

        const selectedLoopHalatKeyForPayload = loopLashingFlag ? (calculationMode === 'calculator' ? manualLoopRopeKey || undefined : effectiveSelectedLoopRope || undefined) : undefined;
        const langKeyMap: Record<string, string> = {
            'tr': 'Türkçe',
            'en': 'English',
            'es': 'Español',
            'de': 'Deutsch',
            'zh': '中文',
            'ja': '日本語'
        };

        const payload: Record<string, any> = {
            calculation_mode: calculationMode,
            language: langKeyMap[lang] || 'Türkçe',
            weight_mt: Number(weight) || 0,
            transport_mode: transportMode,
            transport_unit: transportUnit,
            material: frictionSurface,
            is_oversized_width: isOversizedWidth,
            en: Number(dimensions.width) || 0,
            boy: Number(dimensions.length) || 0,
            yukseklik: Number(dimensions.height) || 0,
            d: Number(cog.height) || 0,
            b_en: Number(cog.width) || 0,
            b_boy: Number(cog.length) || 0,
            breakbulk_flatrack_type: transportUnit === 'breakbulk' ? breakBulkFlatrackType : undefined,
            breakbulk_orientation: transportUnit === 'breakbulk' ? breakBulkOrientation : undefined,
            lashing_display_mode: lashingDisplayMode,
            breakbulk_flatrack_count: transportUnit === 'breakbulk' ? breakBulkFlatrackCount : undefined,
            breakbulk_flatrack_gap: transportUnit === 'breakbulk' ? breakBulkFlatrackGap : undefined,
            use_takoz: woodenDunnageEnabled,
            use_stopper: stopperEnabled,
            takoz_adet_forward: normalizeTakozCount(woodenDunnageData.forces.forward, 0),
            takoz_adet_backward: normalizeTakozCount(woodenDunnageData.forces.backward, 0),
            takoz_adet_right: isOversizedWidth ? 0 : normalizeTakozCount(woodenDunnageData.forces.right, 0),
            takoz_adet_left: isOversizedWidth ? 0 : normalizeTakozCount(woodenDunnageData.forces.left, 0),
            takoz_en: woodenDunnageEnabled ? Number(woodenDunnageData.width) || 0 : 0,
            takoz_yukseklik: woodenDunnageEnabled ? Number(woodenDunnageData.length) || 0 : 0,
            stopper_adet_forward: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.forward) || 0, Number(stopperData.forces.forward) || 0) : 0,
            stopper_adet_backward: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.backward) || 0, Number(stopperData.forces.backward) || 0) : 0,
            stopper_adet_right: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.right) || 0, Number(stopperData.forces.right) || 0) : 0,
            stopper_adet_left: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.left) || 0, Number(stopperData.forces.left) || 0) : 0,
            loop_lashing: loopLashingFlag,
            selected_loop_halat_key: selectedLoopHalatKeyForPayload,
            selected_direct_halatlar: {},
            uygun_direct_halatlar: availableHalats.map(h => ({
                'Lashing Type': h.name,
                'Buckle Type': h.toka,
                'D_Lashing_MSL': h.d_lashing_msl,
                'L_Lashing_MSL': h.l_lashing_msl,
                'MSL Type': h.msl_tur
            })),
            uygun_loop_halatlar: (loopLashingEnabled) ? sortedLoopHalatOptions.map(h => ({
                'Lashing Type': h.name,
                'Buckle Type': h.toka,
                'D_Lashing_MSL': h.cappedDirectMSL,
                'L_Lashing_MSL': h.cappedLoopMSL,
                'MSL Type': h.msl_tur
            })) : [],
            selected_loop_halatlar: (calculationMode === 'calculator' && loopLashingEnabled && manualLoopRopeKey) ? (
                (() => {
                    const matchedRope = sortedLoopHalatOptions.find(h => h.optionKey === manualLoopRopeKey);
                    if (!matchedRope) return [];
                    // Return as a list of rope metadata objects, matching the expected backend format
                    return [{
                        'Halat Türü': matchedRope.name,
                        'TOKA Türü': matchedRope.toka,
                        'D_Lashing_MSL': matchedRope.cappedDirectMSL,
                        'L_Lashing_MSL': matchedRope.cappedLoopMSL,
                        'MSL Türü': matchedRope.msl_tur,
                        'msl_tur': matchedRope.msl_tur
                    }];
                })()
            ) : [],
            lang_dict: {},
            project_description: projectDescription
        };
        if (calculationMode === 'calculator') {
            const manualDirectLashings: Record<string, Array<{ is_active: boolean; count: number; halat_type: string; toka_type: string; lashing_msl: number; position_relative_to_cog: 'below' | 'above'; }>> = {};
            (['forward', 'backward', 'right', 'left'] as const).forEach((dir) => {
                const lashings = manualLashings[dir];
                const lashingList = lashings.map(lashing => {
                    const rope = availableHalats.find((h) => `${h.name}-${h.toka}` === lashing.ropeKey);
                    if (rope && lashing.count > 0) {
                        return {
                            is_active: true,
                            count: lashing.count,
                            halat_type: rope.name,
                            toka_type: rope.toka,
                            lashing_msl: rope.d_lashing_msl,
                            msl_tur: rope.msl_tur,
                            position_relative_to_cog: lashing.position === 'below' ? 'below' : 'above',
                            angle: lashing.angle || 45,
                        };
                    }
                    return null;
                }).filter(Boolean);
                if (lashingList.length > 0) {
                    manualDirectLashings[dir] = lashingList as Array<any>;
                }
            });
            payload.manual_direct_lashings = manualDirectLashings;
            // Ensure manually selected ropes are also added to selected_direct_halatlar
            // so the backend can resolve their metadata (like Loop MSL)
            Object.values(manualDirectLashings).forEach(dirLashings => {
                dirLashings.forEach(l => {
                    const backendKey = `${l.halat_type}_${l.toka_type}`;
                    payload.selected_direct_halatlar[backendKey] = true;
                });
            });
            if (shouldUseManualLoop && manualLoopRopeKey) {
                const loopRope = sortedLoopHalatOptions.find((h) => h.optionKey === manualLoopRopeKey);
                if (loopRope) {
                    payload.manual_loop_lashing = {
                        is_active: true,
                        halat_type: loopRope.name,
                        toka_type: loopRope.toka,
                        count: manualLoopCount,
                        lashing_msl: loopRope.cappedLoopMSL,
                        msl_tur: loopRope.msl_tur,
                    };
                } else if (effectiveSelectedLoopRope) {
                    // Fallback to currently selected rope if manualLoopRopeKey didn't match (prevents silent failure)
                    const fallbackRope = sortedLoopHalatOptions.find(h => h.optionKey === effectiveSelectedLoopRope);
                    if (fallbackRope) {
                        payload.manual_loop_lashing = {
                            is_active: true,
                            halat_type: fallbackRope.name,
                            toka_type: fallbackRope.toka,
                            count: manualLoopCount,
                            lashing_msl: fallbackRope.cappedLoopMSL,
                            msl_tur: fallbackRope.msl_tur,
                        };
                    }
                }
            }
        }
        Object.entries(effectiveSelectedDirectRopes).forEach(([key, isSelected]) => {
            if (isSelected) {
                const [halatTuru, tokaTuru] = key.split('-');
                const backendKey = `${halatTuru}_${tokaTuru}`;
                payload.selected_direct_halatlar[backendKey] = true;
            }
        });
        if (loopLashingFlag && selectedLoopHalatKeyForPayload) {
            payload.selected_loop_halatlar[selectedLoopHalatKeyForPayload] = true;
        }
        return payload;
    }, [cappedDirectHalats, halatTypes, transportUnit, selectedDirectRopes, manualLashings, calculationMode, loopLashingEnabled, selectedLoopRope, sortedLoopHalatOptions, isOversizedWidth, weight, transportMode, frictionSurface, dimensions, cog, breakBulkFlatrackType, breakBulkOrientation, lashingDisplayMode, breakBulkFlatrackCount, breakBulkFlatrackGap, woodenDunnageEnabled, stopperEnabled, woodenDunnageData, stopperData, projectDescription]);

    const validateForm = useCallback(() => {
        if (!transportMode) { setError(t('errors.select_transport_mode')); return false; }
        if (!transportUnit) { setError(t('errors.select_transport_unit')); return false; }

        // Double check unit compatibility for the selected mode
        const isCompatible = validUnits.some(u => u.value === transportUnit);
        if (!isCompatible) {
            setError(t('errors.invalid_transport_unit'));
            return false;
        }

        if (!frictionSurface) { setError(t('errors.select_friction_surface')); return false; }
        if (weight <= 0) { setError(t('errors.invalid_weight')); return false; }
        if (dimensions.width <= 0 || dimensions.length <= 0 || dimensions.height <= 0) { setError(t('errors.invalid_dimensions')); return false; }
        if (cog.width < 0 || cog.length < 0 || cog.height < 0 ||
            cog.width > dimensions.width ||
            cog.length > dimensions.length ||
            cog.height > dimensions.height) {
            setError(t('errors.invalid_cog'));
            return false;
        }
        if (dimensions.width > dimensions.length) { setError(t('errors.cargo_width_greater_than_length')); return false; }
        if (woodenDunnageEnabled && (woodenDunnageData.width <= 0 || woodenDunnageData.length <= 0)) { setError(t('errors.invalid_wooden_dunnage')); return false; }
        if (stopperEnabled && Object.values(stopperData.forces).every(qty => qty <= 0)) { setError('Please enter at least 2 stoppers in at least one direction (e.g. Forward: 2)'); return false; }
        const hasDirectLashing = calculationMode === 'optimizer'
            ? Object.values(selectedDirectRopes).some(v => v)
            : (['forward', 'backward', 'right', 'left'] as const).some(dir =>
                manualLashings[dir].some(l => l.ropeKey && l.count > 0)
            );

        const hasLoopLashing = loopLashingEnabled && (
            calculationMode === 'optimizer'
                ? true
                : (!!manualLashings.loop.ropeKey && manualLashings.loop.count > 0)
        );

        if (!hasDirectLashing && !hasLoopLashing) {
            setError(t('errors.select_at_least_one_rope'));
            return false;
        }
        return true;
    }, [transportMode, transportUnit, validUnits, t, frictionSurface, weight, dimensions, cog, woodenDunnageEnabled, woodenDunnageData, stopperEnabled, stopperData, loopLashingEnabled, calculationMode, selectedDirectRopes, manualLashings]);

    const handleCalculate = useCallback(async () => {
        // Terms Acceptance Check
        if (termsAccepted === false) {
            setError(t('warnings.terms_acceptance_required'));
            return;
        }

        // Loop Lashing Rope Selection Check
        const loopRopeSelected = calculationMode === 'optimizer' 
            ? selectedLoopRope 
            : manualLashings.loop.ropeKey;
        if (loopLashingEnabled && !loopRopeSelected) {
            setError(t('errors.select_rope_type'));
            return;
        }

        // Flatrack Warning Check
        const isFlatrack = transportUnit === 'flatrack_20' || transportUnit === 'flatrack_40';
        const hasForwardDunnage = woodenDunnageEnabled && (woodenDunnageData.forces.forward || 0) > 0;
        const hasBackwardDunnage = woodenDunnageEnabled && (woodenDunnageData.forces.backward || 0) > 0;
        // FIXED: Show warning if EITHER forward OR backward dunnage is 0 (not both)
        const hasDunnageInBothDirections = hasForwardDunnage && hasBackwardDunnage;
        if (isFlatrack && !hasDunnageInBothDirections && !flatrackWarningPending) {
            setShowFlatrackWarning(true);
            return;
        }

        if (isOverweight && !overweightPending) {
            const maxW = selectedTransportUnitData?.max_weight;
            setOverweightWarning(t('warnings.overweight_confirmation').replace('{weight}', weight.toString()).replace('{max_weight}', maxW?.toString() ?? '-'));
            setOverweightPending(true);
            return;
        }
        setOverweightWarning(null);
        setOverweightPending(false);

        // Breakbulk Warning Check
        if (transportUnit === 'breakbulk' && breakbulkSuggested?.warning) {
            setError(breakbulkSuggested.warning);
            return;
        }

        // Dimensions Validation
        if (selectedTransportUnitData) {
            const isContainer = transportUnit.startsWith('container_');
            const isTrailer = transportUnit.startsWith('truck_trailer');
            const isOpenTop = transportUnit.startsWith('open_top_');
            const isFlatrack = transportUnit.startsWith('flatrack_');
            const isLowbed = transportUnit === 'lowbed';
            const isMafi = transportUnit === 'mafi';
            
            const exceededDimensions: string[] = [];
            const exceededDimKeys: ('width' | 'length' | 'height')[] = [];

            // Rule 1 & 8: Konteyner'ler ve Dorse'ler -> En-Boy-Yükseklik taşmalarına izin verilmez.
            if (isContainer || isTrailer) {
                if (dimensions.width > selectedTransportUnitData.width) { exceededDimensions.push(t('errors.dim_width')); exceededDimKeys.push('width'); }
                if (dimensions.length > selectedTransportUnitData.length) { exceededDimensions.push(t('errors.dim_length')); exceededDimKeys.push('length'); }
                if (dimensions.height > selectedTransportUnitData.height) { exceededDimensions.push(t('errors.dim_height')); exceededDimKeys.push('height'); }
            }
            // Rule 2: Open Top'lar -> En-Boy taşmalarına izin verilmez. Yükseklik taşmasına izin verilir.
            else if (isOpenTop) {
                if (dimensions.width > selectedTransportUnitData.width) { exceededDimensions.push(t('errors.dim_width')); exceededDimKeys.push('width'); }
                if (dimensions.length > selectedTransportUnitData.length) { exceededDimensions.push(t('errors.dim_length')); exceededDimKeys.push('length'); }
                // Height can overhang -> no check needed.
            }
            // Rule 3, 5, 6: Flatrack'ler, Lowbed, Mafi -> Boy taşmasına izin verilmez. En-Yükseklik taşmasına izin verilir.
            else if (isFlatrack || isLowbed || isMafi) {
                if (dimensions.length > selectedTransportUnitData.length) {
                    setExceededDimsInfo({ dims: ['length'], unitName: t(`transport_units.${transportUnit}`) });
                    setError(t('errors.invalid_cargo_dimensions'));
                    return;
                }
            }
            // Rule 4 & 7: Breakbulk, Gemi -> Kısıtlama yok.

            if (exceededDimensions.length > 0) {
                const dimStr = exceededDimensions.join(t('errors.and_conjunction'));
                setExceededDimsInfo({ dims: exceededDimKeys, unitName: t(`transport_units.${transportUnit}`) });
                setError(t('errors.dimension_exceeded').replace('{dims}', dimStr));
                return;
            }
        }

        setError(null);
        setExceededDimsInfo(null);
        setResults(null);
        setOptimizerResultsTransferred(false);
        if (!validateForm()) return;
        setIsCalculating(true);
        try {
            const payload = prepareCalculationData();
            const data = await apiPost<any>('/api/calculate', payload);
            if (data && typeof data === 'object') {
                // Ensure context is available for ResultsTable
                const resultsWithContext = {
                    ...data,
                    transport_unit: payload.transport_unit,
                    use_takoz: payload.use_takoz,
                    is_oversized_width: payload.is_oversized_width
                };

                // Swap lashing labels to Cordstrap names when display mode is 'cordstrap'
                if (lashingDisplayMode === 'cordstrap' && resultsWithContext.lashing_details) {
                    // Build msl_tur → cordstrap_name lookup from all loaded halats
                    const allHalats = [...halatTypes, ...cappedDirectHalats];
                    const cordstrapMap: Record<string, string> = {};
                    allHalats.forEach(h => {
                        if (h.cordstrap_name && h.msl_tur) {
                            cordstrapMap[h.msl_tur] = h.cordstrap_name;
                        }
                        // Also map by name-toka combo
                        if (h.cordstrap_name && h.name) {
                            cordstrapMap[h.name] = h.cordstrap_name;
                        }
                    });

                    const details = resultsWithContext.lashing_details;
                    // Swap direct lashing labels
                    (['forward', 'backward', 'right', 'left'] as const).forEach(dir => {
                        details.direct_lashings[dir]?.forEach((l: any) => {
                            const csName = cordstrapMap[l.msl_tur] || cordstrapMap[l.type];
                            if (csName) {
                                l.msl_tur = csName;
                            }
                        });
                    });
                    // Swap loop lashing label
                    if (details.loop_lashing) {
                        const csName = cordstrapMap[details.loop_lashing.msl_tur] || cordstrapMap[details.loop_lashing.type];
                        if (csName) {
                            details.loop_lashing.msl_tur = csName;
                        }
                    }
                }

                // Synchronize selectedLoopRope with Optimizer result if in optimizer mode
                if (calculationMode === 'optimizer' && resultsWithContext.selected_loop_halat) {
                    const loopH = resultsWithContext.selected_loop_halat;
                    const ropeKey = `${loopH['Lashing Type']}-${loopH['Buckle Type']}`;
                    const exists = sortedLoopHalatOptions.some(h => h.optionKey === ropeKey);
                    if (exists && selectedLoopRope !== ropeKey) {
                        setSelectedLoopRope(ropeKey);
                    }
                }

                // NEW: Fetch Diagram Geometry for SVG Rendering
                try {
                    const diagramPayload = {
                        load: {
                            width: dimensions.width / 100, // Conversion to meters for backend
                            height: dimensions.height / 100,
                            x: 2.0, y: 2.0, // Backend defaults
                            weight: `${weight} kg`,
                            authority: 'Lashing App'
                        },
                        wedges: {
                            enabled: stopperEnabled || woodenDunnageEnabled,
                            left: { count: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.left) || 0, 0) : (woodenDunnageEnabled ? (isOversizedWidth ? 0 : normalizeTakozCount(woodenDunnageData.forces.left, 0)) : 0) },
                            right: { count: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.right) || 0, 0) : (woodenDunnageEnabled ? (isOversizedWidth ? 0 : normalizeTakozCount(woodenDunnageData.forces.right, 0)) : 0) },
                            top: { count: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.forward) || 0, 0) : (woodenDunnageEnabled ? normalizeTakozCount(woodenDunnageData.forces.forward, 0) : 0) },
                            bottom: { count: stopperEnabled ? normalizeStopperCount(Number(stopperData.forces.backward) || 0, 0) : (woodenDunnageEnabled ? normalizeTakozCount(woodenDunnageData.forces.backward, 0) : 0) },
                            stopper_enabled: stopperEnabled
                        },
                        lashings: resultsWithContext.lashing_details?.direct_lashings || {},
                        looplash: {
                            enabled: loopLashingEnabled,
                            count: resultsWithContext.lashing_details?.loop_lashing?.count || 0,
                            type: resultsWithContext.lashing_details?.loop_lashing?.type || 'Standard'
                        },
                        transport_unit: transportUnit,
                        breakbulk_orientation: breakBulkOrientation
                    };
                    const diagramData = await apiPost<any>('/api/diagram/data', diagramPayload);
                    if (diagramData) {
                        resultsWithContext.lashing_diagram_data = diagramData;
                    }
                } catch (diagError) {
                    appLogger.error('Failed to fetch high-fidelity diagram data', diagError as Error);
                }

                setResults(resultsWithContext);
                setError(null);
                hapticFeedback('medium');

                // Save snapshot to IndexedDB for offline access (best-effort, never throws)
                saveCalculationOffline({
                    id: `calc-${Date.now()}`,
                    timestamp: new Date().toISOString(),
                    summary: [
                        weight ? `${weight}T` : null,
                        transportUnit || null,
                        transportMode || null,
                    ].filter(Boolean).join(' / '),
                    inputs: { weight, dimensions, transportMode, transportUnit, frictionSurface },
                    results: { summary: resultsWithContext?.optimization_summary ?? {} },
                });

                if (!device.isDesktop) {
                    setMobileTab('results');
                }
            } else {
                throw new Error('Invalid response data received from server');
            }
        } catch (e: unknown) {
            const error = e as any;
            let errorMessage = error.message || 'Error occurred';
            if (error.response?.data?.detail) errorMessage = error.response.data.detail;
            setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
            setResults(null);
        } finally {
            setIsCalculating(false);
        }
    }, [termsAccepted, t, transportUnit, woodenDunnageEnabled, flatrackWarningPending, isOverweight, overweightPending, selectedTransportUnitData, weight, dimensions, validateForm, prepareCalculationData, lashingDisplayMode, halatTypes, cappedDirectHalats]);

    // Auto-trigger calculation when user confirms Flatrack warning
    useEffect(() => {
        if (flatrackWarningPending) {
            setFlatrackWarningPending(false); // Reset flag BEFORE calculation to prevent loop
            handleCalculate();
        }
    }, [flatrackWarningPending, handleCalculate]);

    const handlePDFDownloadClick = () => {
        setIsProjectModalOpen(true);
    };

    const handleEditOptimizerResult = () => {
        if (!results || !results.lashing_details) return;
        const details = results.lashing_details;
        const newManualLashings: ManualLashings = { ...initialManualLashings };

        // Helper to strip technical suffixes like " (L_Lashing_MSL: 35.25)" or " (D_Lashing_MSL: 33.25)"
        // This ensures the ropeKey matches the frontend's optionKey format: `${h.name}-${h.toka}`
        const cleanRopeType = (type: string) => {
            if (!type) return "";
            // Strip everything from the first " (" onwards
            const bracketIndex = type.indexOf(' (');
            if (bracketIndex !== -1) {
                return type.substring(0, bracketIndex).trim();
            }
            return type.trim();
        };

        // Map lashing_details (3D-oriented) back to logical Force directions
        // Note: forward in 3D details contains Direction.BACKWARD protection data (physically at front)
        // So UI 'forward' (Against Forward Forces) needs Direction.FORWARD data, which is in 3D 'backward' key.
        // For oversized width cargo, lateral directions in 3D match the physical sides directly (right=right, left=left)
        const logicalToBackendMap: Record<string, 'forward' | 'backward' | 'right' | 'left'> = {
            forward: 'backward',
            backward: 'forward',
            right: isOversizedWidth ? 'right' : 'left',
            left: isOversizedWidth ? 'left' : 'right'
        };

        (['forward', 'backward', 'right', 'left'] as const).forEach(dir => {
            const backendKey = logicalToBackendMap[dir];
            const backendLashings = details.direct_lashings[backendKey] || [];
            
            newManualLashings[dir] = backendLashings.map((l: any, idx: number) => ({
                id: `${dir}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
                ropeKey: `${cleanRopeType(l.type)}-${l.buckle}`,
                count: l.count,
                position: l.position || 'above',
                angle: (dir === 'right' || dir === 'left') ? (l.angle || 45) : undefined
            }));

            // Ensure at least one row exists if empty
            if (newManualLashings[dir].length === 0) {
                newManualLashings[dir] = [{ id: Math.random().toString(36).substr(2, 9), ropeKey: '', count: 0, position: 'above' }];
            }
        });

        // Transfer loop lashing
        if (details.loop_lashing && details.loop_lashing.enabled) {
            const backendType = details.loop_lashing.type; // e.g. "MSL 3750daN (L_Lashing_MSL: 35.25)"
            const backendBuckle = details.loop_lashing.buckle;
            
            // Robust matching: find the actual option that match.
            // Loop ropes are uniquely identified by their name (ignoring technical MSL suffixes) and buckle.
            const matchedOption = sortedLoopHalatOptions.find(opt => {
                const cleanOptName = cleanRopeType(opt.name); // Usually already clean, but for safety
                const cleanBackendType = cleanRopeType(backendType);
                return cleanOptName === cleanBackendType && opt.toka === backendBuckle;
            });

            if (matchedOption) {
                const foundRopeKey = matchedOption.optionKey;
                newManualLashings.loop = {
                    ropeKey: foundRopeKey,
                    count: (details.loop_lashing.count || 0) / 2,
                    position: 'below'
                };
                setLoopLashingEnabled(true);
                setSelectedLoopRope(foundRopeKey); // SYNC UI DROPDOWN
            } else {
                // Last resort fallback string matching if options list is empty or weird
                const fallbackKey = `${cleanRopeType(backendType)}-${backendBuckle}`;
                newManualLashings.loop = {
                    ropeKey: fallbackKey,
                    count: (details.loop_lashing.count || 0) / 2,
                    position: 'below'
                };
                setLoopLashingEnabled(true);
                setSelectedLoopRope(fallbackKey); 
            }
        } else {
            setLoopLashingEnabled(false);
            newManualLashings.loop = { ropeKey: '', count: 0, position: 'below' };
            setSelectedLoopRope(null);
        }

        isEditingOptimizerResult.current = true;
        setManualLashings(newManualLashings);
        setCalculationMode('calculator');
        setOptimizerResultsTransferred(true);
        setError(null);
    };

    const downloadProfessionalPDF = async (projectDescriptionFromModal: string, retryCount = 0) => {
        setIsProjectModalOpen(false);
        setIsGeneratingPDF(true);
        setProjectDescription(projectDescriptionFromModal);
        try {
            // Capture 3D views if available
            let lashing3dViews: { backwardRight: string; forwardLeft: string } | null = null;
            if (lashingCardRef.current && enable3DLashingView) {
                try {
                    lashing3dViews = await lashingCardRef.current.capture3DViews();
                } catch (captureError) {
                    console.warn('Failed to capture 3D views:', captureError);
                }
            }

            const payload = {
                ...prepareCalculationData(),
                project_description: projectDescriptionFromModal,
                lashing_3d_views: lashing3dViews,
                lashing_display_mode: lashingDisplayMode,
                pdf_theme: pdfTheme
            };
            const result = await apiPost<{ download_url: string, report_id?: string }>('/api/generate_pdf', payload);
            const downloadFilename = result.report_id ? `${result.report_id}.pdf` : `lashing-plan-${new Date().toISOString().split('T')[0]}.pdf`;
            if (result.download_url) {
                const cargoSummaryText = `${weight} MT • ${transportMode || ''} / ${transportUnit || ''}`;
                const savedRecord = savePdfReport({
                    id: result.report_id || `pdf_${Date.now()}`,
                    name: downloadFilename,
                    date: new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
                    url: result.download_url,
                    projectDescription: projectDescriptionFromModal,
                    cargoSummary: cargoSummaryText
                });

                setSelectedPdfReport(savedRecord);
                setIsPdfViewerModalOpen(true);

                if (result.download_url.startsWith('data:application/pdf;base64,')) {
                    const base64Data = result.download_url.split(',')[1];
                    const binaryData = atob(base64Data);
                    const bytes = new Uint8Array(binaryData.length);
                    for (let i = 0; i < binaryData.length; i++) bytes[i] = binaryData.charCodeAt(i);
                    const blob = new Blob([bytes], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = downloadFilename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                } else {
                    // It's a remote URL from Supabase. Fetch via blob to prevent browser navigation flashes.
                    try {
                        const response = await fetch(result.download_url);
                        if (!response.ok) throw new Error('Failed to fetch remote PDF');
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        // a.download only works beautifully strictly for same-origin or blob URLs
                        a.download = downloadFilename;
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => {
                            document.body.removeChild(a);
                            window.URL.revokeObjectURL(url);
                        }, 100);
                    } catch (fetchError) {
                        console.error('Blob fetch failed, falling back to open tab:', fetchError);
                        // Fallback if CORS prevents blob fetch
                        const a = document.createElement('a');
                        a.href = `${result.download_url}?download=true`; 
                        a.target = '_blank'; 
                        a.download = downloadFilename;
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => document.body.removeChild(a), 100);
                    }
                }
            } else {
                throw new Error('No download URL received');
            }
        } catch (error: any) {
            if (retryCount < 2) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return downloadProfessionalPDF(projectDescriptionFromModal, retryCount + 1);
            }
            console.error('PDF generation error:', error);
            setError(`PDF download failed: ${error.message || 'Please try again.'}`);
        }
        finally {
            setIsGeneratingPDF(false);
        }
    };

    if (!uiDataLoaded) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-red-50 text-gray-900">
                <div className="text-center max-w-md mx-auto p-6">
                    {dataError ? (
                        <div>
                            <div className="text-red-500 text-6xl mb-4">⚠️</div>
                            <h2 className="text-3xl mb-4 text-red-600">Veri Yükleme Hatası</h2>
                            <p className="text-gray-700 text-lg mb-6">{dataError}</p>
                            <button onClick={() => window.location.reload()} className="bg-orange-600 hover:bg-orange-700 px-6 py-2 rounded-lg font-medium transition-colors text-white">Tekrar Dene</button>
                        </div>
                    ) : (
                        <LoadingState message={t('loading_data') || 'Loading application data...'} showSkeleton={false} onCancel={() => window.location.reload()} />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F1F5F9] dark:bg-[#111827] transition-colors duration-300 pb-36 lg:pb-0">
            <AppHeader 
                lang={lang} 
                setLang={setLang} 
                onLogout={onLogout} 
                theme={uiTheme} 
                onToggleTheme={handleThemeToggle} 
                onOpenSavedPdfs={() => setIsSavedPdfModalOpen(true)} 
                onOpenRecentCalcs={() => setIsRecentCalculationsModalOpen(true)}
            />
            {dataError && (<div className="max-w-[1920px] mx-auto mt-4 px-4"><div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center gap-3 text-rose-800 dark:bg-rose-950/30 dark:border-rose-800/50 dark:text-rose-200"><AlertTriangle className="w-5 h-5 text-rose-500" />{dataError}</div></div>)}

            <main className="max-w-[1920px] mx-auto p-2 lg:p-4 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
                <div className={`order-1 lg:row-span-2 h-fit ${(!device.isDesktop && mobileTab !== 'securing') ? 'hidden lg:block' : ''}`}>
                    <Sidebar
                        woodenDunnageEnabled={woodenDunnageEnabled}
                        setWoodenDunnageEnabled={handleWoodenDunnageToggle}
                        stopperEnabled={stopperEnabled}
                        setStopperEnabled={handleStopperToggle}
                        loopLashingEnabled={loopLashingEnabled}
                        setLoopLashingEnabled={handleLoopLashingToggle}
                        selectedLoopRope={selectedLoopRope || manualLashings.loop.ropeKey}
                        setSelectedLoopRope={setSelectedLoopRope}
                        selectedDirectRopes={selectedDirectRopes}
                        setSelectedDirectRopes={setSelectedDirectRopes}
                        halatTypes={cappedDirectHalats.length > 0 ? cappedDirectHalats : (halatTypes || [])}
                        transportUnit={transportUnit}
                        isVesselSelected={isVesselSelected}
                        sortedLoopHalatOptions={sortedLoopHalatOptions}
                        onCalculate={handleCalculate}
                        isCalculating={isCalculating}
                        results={results}
                        downloadProfessionalPDF={handlePDFDownloadClick}
                        t={t}
                        overweightPending={overweightPending}
                        calculationMode={calculationMode}
                        setCalculationMode={handleCalculationModeChange}
                        manualLashings={manualLashings}
                        setManualLashings={setManualLashings}
                        isAdmin={isAdmin}
                        onCalculationReset={handleCalculationReset}
                        lashingDisplayMode={lashingDisplayMode}
                        optimizerResultsTransferred={optimizerResultsTransferred}
                    />
                </div>
                <div className={`order-2 w-full flex flex-col gap-3 min-w-0 ${(!device.isDesktop && mobileTab === 'securing') ? 'hidden lg:flex' : ''}`}>
                    <div className={`bg-white dark:bg-[#182235] rounded-2xl border-2 border-orange-500/10 dark:border-slate-400/20 shadow-xl shadow-slate-200/50 dark:shadow-slate-950/30 overflow-hidden transition-all duration-300 ${(!device.isDesktop && mobileTab !== 'inputs' && mobileTab !== 'results') ? 'hidden lg:block' : ''}`}>
                        <div className="p-3 lg:p-5">
                            {(device.isDesktop || mobileTab === 'inputs') && (
                                <>
                                    <div className="mb-2">
                            <div className="flex flex-col sm:flex-row flex-wrap items-end gap-2 justify-center sm:justify-start">
                                <div className="w-full sm:flex-1 sm:min-w-[120px] flex justify-center sm:block">
                                    <div className="w-1/2 sm:w-full">
                                        <NumericStepper label={t('weight')} value={weight} onChange={(value) => { setWeight(value); setResults(null); setError(null); }} min={0} step={0.5} isWarning={isOverweight} />
                                    </div>
                                </div>
                                <div className="w-full sm:flex-1 sm:min-w-[140px]">
                                    <CustomDropdown label={t('transport_mode')} value={transportMode} placeholder={t('select')} options={transportModes.map(mode => ({ value: mode.value, label: t(`transport_modes.${mode.value}`) !== `transport_modes.${mode.value}` ? t(`transport_modes.${mode.value}`) : mode.label }))} onChange={(selected) => { setTransportMode(selected); setResults(null); setError(null); }} compact={true} />
                                </div>
                                <div className="w-full sm:flex-1 sm:min-w-[200px]">
                                    <CustomDropdown 
                                        label={t('transport_unit')} 
                                        value={transportUnit} 
                                        placeholder={transportMode ? t('select') : t('select_transport_mode_first')} 
                                        options={validUnits.map(unit => {
                                            let groupKey = 'special';
                                            if (unit.value.includes('container') && !unit.value.includes('open_top')) groupKey = 'containers';
                                            else if (unit.value.includes('open_top')) groupKey = 'open_tops';
                                            else if (unit.value.includes('flatrack')) groupKey = 'flatracks';
                                            else if (unit.value.includes('truck_trailer')) groupKey = 'trailers';
                                            
                                            const defaultGroupTrans = groupKey === 'special' ? 'Others / Special' : groupKey.charAt(0).toUpperCase() + groupKey.slice(1).replace('_', ' ');
                                            const groupTrans = t(`transport_unit_groups.${groupKey}`, defaultGroupTrans);
                                            
                                            return { 
                                                value: unit.value, 
                                                group: typeof groupTrans === 'string' && groupTrans !== `transport_unit_groups.${groupKey}` ? groupTrans : defaultGroupTrans,
                                                label: t(`transport_units.${unit.value}`) !== `transport_units.${unit.value}` ? t(`transport_units.${unit.value}`) : unit.label, 
                                                description: unit.max_weight ? `${t('labels.max_weight') || 'Max'}: ${unit.max_weight} MT` : undefined 
                                            };
                                        })} 
                                        onChange={(selected) => { setTransportUnit(selected); setResults(null); setError(null); }} 
                                        disabled={!transportMode} 
                                        emptyStateText={transportMode ? t('no_options') : t('select_transport_mode_first')} 
                                        compact={true} 
                                    />
                                </div>
                                <div className="w-full sm:flex-1 sm:min-w-[160px]">
                                    <CustomDropdown label={t('friction_surface')} value={frictionSurface} placeholder={t('select')} options={materials.map(material => ({ value: material.value, label: t(`materials.${material.value}`) !== `materials.${material.value}` ? t(`materials.${material.value}`) : material.label }))} onChange={(selected) => { setFrictionSurface(selected); setResults(null); setError(null); }} compact={true} />
                                </div>
                            </div>
                        </div>

                        <div className="mb-2">
                            <div className="flex justify-between items-center mb-1 gap-2">
                                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 whitespace-nowrap">{t('cargo_dimensions')}</h3>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 items-end gap-x-5 gap-y-3 sm:gap-2.5 lg:gap-1">
                                <div className="w-full"><NumericStepper label={t('length')} value={dimensions.length} onChange={(value) => handleDimensionChange('length', value.toString())} min={0} step={0.5} disableWheel={true} /></div>
                                <div className="w-full"><NumericStepper label={t('width')} value={dimensions.width} onChange={(value) => handleDimensionChange('width', value.toString())} min={0} step={0.5} disableWheel={true} /></div>
                                <div className="w-full"><NumericStepper label={t('height')} value={dimensions.height} onChange={(value) => handleDimensionChange('height', value.toString())} min={0} step={0.5} disableWheel={true} /></div>
                                <div className="w-full"><NumericStepper label={t('cog_length')} value={parseFloat(cogDisplay.length)} onChange={(value) => handleCogChange('length', value.toString())} min={Number((dimensions.length * 0.1).toFixed(2))} max={Number((dimensions.length * 0.9).toFixed(2))} step={0.1} disableWheel={true} tooltip={t('tooltips.cog_info')} /></div>
                                <div className="w-full"><NumericStepper label={t('cog_width')} value={parseFloat(cogDisplay.width)} onChange={(value) => handleCogChange('width', value.toString())} min={Number((dimensions.width * 0.1).toFixed(2))} max={Number((dimensions.width * 0.9).toFixed(2))} step={0.1} disableWheel={true} tooltip={t('tooltips.cog_info')} /></div>
                                <div className="w-full"><NumericStepper label={t('cog_height')} value={parseFloat(cogDisplay.height)} onChange={(value) => handleCogChange('height', value.toString())} min={Number((dimensions.height * 0.1).toFixed(2))} max={Number((dimensions.height * 0.9).toFixed(2))} step={0.1} disableWheel={true} tooltip={t('tooltips.cog_info')} /></div>
                            </div>
                        </div>

                        {transportUnit === 'breakbulk' && (
                            <div className="mb-3 p-3 bg-orange-50/30 dark:bg-orange-900/10 border border-orange-200/50 dark:border-orange-500/20 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0"></div>
                                        <h4 className="text-xs font-bold text-orange-700 dark:text-orange-400 tracking-wider whitespace-nowrap">{t('labels.breakbulk_settings')}</h4>
                                    </div>

                                    {/* Breakbulk warning banner */}
                                    {breakbulkSuggested?.warning && (
                                        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-300 dark:border-rose-600 text-rose-700 dark:text-rose-400 text-xs font-semibold">
                                            <span>{breakbulkSuggested.warning}</span>
                                        </div>
                                    )}

                                    {/* Row 1: Type, Orientation, Count, and Split Display */}
                                    <div className="flex flex-col sm:flex-row lg:flex-nowrap gap-3 items-center w-full">
                                        <div className="w-full sm:w-1/2 lg:w-1/4">
                                            <CustomDropdown
                                                label={t('labels.flatrack_type')}
                                                value={breakBulkFlatrackType}
                                                options={[
                                                    { value: 'flatrack_20', label: t('transport_units.flatrack_20') },
                                                    { value: 'flatrack_40', label: t('transport_units.flatrack_40') }
                                                ]}
                                                onChange={(selected) => { setBreakBulkFlatrackType(selected as any); setResults(null); }}
                                                compact={true}
                                            />
                                        </div>
                                        <div className="w-full sm:w-1/2 lg:w-1/4">
                                            <CustomDropdown
                                                label={t('labels.orientation')}
                                                value={breakBulkOrientation}
                                                options={[
                                                    { value: 'longitudinal', label: t('labels.longitudinal') },
                                                    { value: 'transverse', label: t('labels.transverse') }
                                                ]}
                                                onChange={(selected) => { setBreakBulkOrientation(selected as any); setResults(null); }}
                                                compact={true}
                                            />
                                        </div>
                                        <div className="w-full sm:w-1/2 lg:w-1/4">
                                            <NumericStepper
                                                label={t('labels.flatrack_gap')}
                                                value={breakBulkFlatrackGap}
                                                step={0.05}
                                                onChange={(val) => {
                                                    setBreakBulkFlatrackGap(Math.max(0.15, val));
                                                    setResults(null);
                                                }}
                                                min={0.15}
                                                max={Number(((breakBulkOrientation === 'transverse' ? dimensions.length : dimensions.width) * 0.9).toFixed(2))}
                                            />
                                        </div>
                                        <div className="w-full sm:w-1/2 lg:w-1/4">
                                            <NumericStepper
                                                label={`${t('labels.flatrack_count')}${breakbulkSuggested ? ` (${breakbulkSuggested.recommendedCount})` : ''}`}
                                                value={breakBulkFlatrackCount}
                                                onChange={(val) => { 
                                                    setBreakBulkFlatrackCount(Math.max(2, val)); 
                                                    setResults(null); 
                                                }}
                                                min={2}
                                                max={20}
                                            />
                                        </div>

                                    </div>
                                    
                                    {/* Flatrack Count Warning */}
                                    {breakbulkSuggested && breakBulkFlatrackCount < breakbulkSuggested.recommendedCount && (
                                        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 text-xs font-semibold mt-1">
                                            <span>⚠️ {t('warnings.flatrack_count_low').replace('{selected}', breakBulkFlatrackCount.toString()).replace('{recommended}', breakbulkSuggested.recommendedCount.toString())}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Wooden Dunnage & Stopper inputs omitted for brevity but logic is preserved in state */}
                        {!isVesselSelected && woodenDunnageEnabled && (
                            <div className="p-3 border border-slate-600 rounded-lg">
                                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-1">{t('wooden_dunnage_dimensions')}</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-5 gap-y-3 sm:gap-2.5 lg:gap-1.5">
                                    <div className="w-full"><NumericStepper label={t('labels.wooden_dunnage_length')} value={woodenDunnageData.length} onChange={(value) => { setWoodenDunnageData(prev => ({ ...prev, length: value })); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="w-full"><NumericStepper label={t('labels.wooden_dunnage_width')} value={woodenDunnageData.width} onChange={(value) => { setWoodenDunnageData(prev => ({ ...prev, width: value })); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="w-full lg:border-l-2 lg:border-orange-400 lg:pl-3"><NumericStepper label={t('labels.forward')} value={woodenDunnageData.forces.forward} onChange={(value) => { setWoodenDunnageData(prev => ({ ...prev, forces: { ...prev.forces, forward: normalizeTakozCount(value, prev.forces.forward) } })); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="w-full"><NumericStepper label={t('labels.backward')} value={woodenDunnageData.forces.backward} onChange={(value) => { setWoodenDunnageData(prev => ({ ...prev, forces: { ...prev.forces, backward: normalizeTakozCount(value, prev.forces.backward) } })); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="w-full">
                                        <NumericStepper
                                            label={t('labels.right')}
                                            value={isOversizedWidth ? 0 : woodenDunnageData.forces.right}
                                            onChange={(value) => { setWoodenDunnageData(prev => ({ ...prev, forces: { ...prev.forces, right: normalizeTakozCount(value, prev.forces.right) } })); handleCalculationReset(); }}
                                            min={0}
                                            disableWheel={true}
                                            disabled={isOversizedWidth}
                                        />
                                    </div>
                                    <div className="w-full">
                                        <NumericStepper
                                            label={t('labels.left')}
                                            value={isOversizedWidth ? 0 : woodenDunnageData.forces.left}
                                            onChange={(value) => { setWoodenDunnageData(prev => ({ ...prev, forces: { ...prev.forces, left: normalizeTakozCount(value, prev.forces.left) } })); handleCalculationReset(); }}
                                            min={0}
                                            disableWheel={true}
                                            disabled={isOversizedWidth}
                                        />
                                    </div>
                                </div>
                                {isOversizedWidth && (
                                    <p className="text-[10px] text-orange-600 dark:text-orange-400 mt-1 font-medium italic">
                                        {t('warnings.oversized_width_side_wedges_disabled')}
                                    </p>
                                )}
                            </div>
                        )}
                        {isVesselSelected && stopperEnabled && (
                            <div className="p-3 border border-slate-600 rounded-lg">
                                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-1">{t('stopper')}</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-3 sm:gap-2.5 lg:gap-1.5 mb-2">
                                    <div className="flex-1"><NumericStepper label={t('labels.forward')} value={stopperData.forces.forward} onChange={(value) => { setStopperData(prev => { const normalized = normalizeStopperCount(value, prev.forces.forward); return { ...prev, forces: { ...prev.forces, forward: normalized } }; }); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="flex-1"><NumericStepper label={t('labels.backward')} value={stopperData.forces.backward} onChange={(value) => { setStopperData(prev => { const normalized = normalizeStopperCount(value, prev.forces.backward); return { ...prev, forces: { ...prev.forces, backward: normalized } }; }); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="flex-1"><NumericStepper label={t('labels.right')} value={stopperData.forces.right} onChange={(value) => { setStopperData(prev => { const normalized = normalizeStopperCount(value, prev.forces.right); return { ...prev, forces: { ...prev.forces, right: normalized } }; }); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                    <div className="flex-1"><NumericStepper label={t('labels.left')} value={stopperData.forces.left} onChange={(value) => { setStopperData(prev => { const normalized = normalizeStopperCount(value, prev.forces.left); return { ...prev, forces: { ...prev.forces, left: normalized } }; }); handleCalculationReset(); }} min={0} disableWheel={true} /></div>
                                </div>
                            </div>
                        )}
                                    </>
                                )}
                        {isCalculating && (
                            <div className="min-h-[200px] bg-gray-50 dark:bg-gradient-to-br dark:from-[#0D111C] dark:to-[#070910] rounded-xl p-1 border-2 border-orange-100 dark:border-white/10 mt-2">
                                <div className="text-center py-12">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-cyan-500 shadow-lg shadow-sky-500/50 mb-4 animate-pulse">
                                        <svg className="animate-spin h-8 w-8 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    </div>
                                    <p className="text-slate-300 font-medium">{t('common.processing')}</p>
                                </div>
                            </div>
                        )}
                        {(device.isDesktop || mobileTab === 'inputs') && !results && !isCalculating && !error && (
                            <div className="w-full mt-2">
                                {show3DView ? (
                                    <div className="p-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.02] shadow-md transition-all duration-300">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-2">
                                            <Checkbox
                                                label={<span className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">{t('app.cog3d.title')}</span>}
                                                checked={show3DView}
                                                onChange={(checked) => setShow3DView(checked)}
                                            />
                                            <span className="text-[11px] text-slate-500 dark:text-slate-400 italic">{t('app.cog3d.subtitle')}</span>
                                        </div>
                                        <div className="w-full max-w-6xl h-[320px] sm:h-[400px] mx-auto relative ring-1 ring-orange-500/10 dark:ring-orange-500/5 rounded-xl overflow-hidden transition-all duration-500">
                                            <CogCard 
                                                width={(transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (dimensions.width || 2.4) : (dimensions.length || 6)} 
                                                depth={(transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (dimensions.length || 6) : (dimensions.width || 2.4)} 
                                                height={dimensions.height || 2.6} 
                                                weight={weight} 
                                                cog={cog ? {
                                                    width: (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (cog.length || 0) : (cog.width || 0),
                                                    length: (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (cog.width || 0) : (cog.length || 0),
                                                    height: cog.height || 0
                                                } : undefined}
                                                className="w-full h-full absolute inset-0" 
                                                theme={uiTheme} 
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center py-1 px-1">
                                        <Checkbox
                                            label={<span className="text-xs font-medium text-slate-600 dark:text-slate-400">{t('app.cog3d.title')}</span>}
                                            checked={show3DView}
                                            onChange={(checked) => setShow3DView(checked)}
                                        />
                                    </div>
                                )}
                                {overweightWarning && <p className="text-red-400 text-center mt-4 text-xl whitespace-pre-line">{overweightWarning}</p>}
                            </div>
                        )}
                        {results && (
                            <div className="space-y-1">
                                {/* Mobile Segmented Sub-Tab Toggle */}
                                {!device.isDesktop && (
                                    <div className="flex bg-slate-100 dark:bg-slate-900/60 p-1 rounded-xl gap-1 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setMobileResultSubTab('table')}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                                mobileResultSubTab === 'table'
                                                    ? 'bg-white dark:bg-[#0D111C] text-orange-600 dark:text-orange-400 shadow-sm border border-slate-200/50 dark:border-white/5'
                                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                            }`}
                                        >
                                            <BarChart3 className="w-4 h-4" />
                                            {tr('results.table_tab', 'Kuvvet Tablosu')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setMobileResultSubTab('diagram')}
                                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                                mobileResultSubTab === 'diagram'
                                                    ? 'bg-white dark:bg-[#0D111C] text-orange-600 dark:text-orange-400 shadow-sm border border-slate-200/50 dark:border-white/5'
                                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                            }`}
                                        >
                                            <Settings2 className="w-4 h-4" />
                                            {tr('results.diagram_tab', 'Bağlama Çizimi & 3D')}
                                        </button>
                                    </div>
                                )}

                                {/* Force Analysis Table */}
                                {(device.isDesktop || mobileResultSubTab === 'table') && (
                                    <div>
                                        <ResultsTable
                                            t={t}
                                            data={results?.results_df}
                                            response={results}
                                            userEmail={userEmail}
                                            userRole={userRole}
                                            lashingDisplayMode={lashingDisplayMode}
                                            woodenDunnageData={woodenDunnageData}
                                        />
                                    </div>
                                )}

                                {/* Diagram & 3D Section */}
                                {(device.isDesktop || mobileResultSubTab === 'diagram') && (
                                    <div className="space-y-4 pt-1">
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2 px-1">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('lashing_diagram')}</h3>
                                                {enable3DLashingView && <Checkbox label={<span className="text-sm font-medium">3D View</span>} checked={show3DLashingDiagram} onChange={(checked) => setShow3DLashingDiagram(checked)} />}
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => { hapticFeedback('light'); setIsSavedPdfModalOpen(true); }}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-xs font-bold rounded-xl border border-orange-500/20 transition-all active:scale-95 shadow-sm"
                                                    title="Kaydedilmiş PDF Raporları"
                                                >
                                                    <FileText className="w-3.5 h-3.5" />
                                                    <span>{tr('results.pdf_reports_history', 'PDF Raporlarım')}</span>
                                                </button>

                                                {calculationMode === 'optimizer' && (
                                                    <button
                                                        onClick={handleEditOptimizerResult}
                                                        className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-slate-700 to-slate-800 dark:from-white/10 dark:to-white/5 hover:from-slate-600 hover:to-slate-700 dark:hover:bg-white/20 text-white dark:text-orange-400 text-xs font-bold rounded-lg shadow-md shadow-slate-900/40 dark:shadow-none transition-all duration-300 transform hover:scale-105 active:scale-95 border dark:border-white/10"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" className="dark:text-orange-400">
                                                            <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.707-6.707zM3.37 9L1 11.37a.5.5 0 0 0-.107.575l1.247 2.494A1.5 1.5 0 0 0 3.642 15h2.494a.5.5 0 0 0 .575-.107l2.37-2.37H9v-.5a.5.5 0 0 1 .5-.5h.5a.5.5 0 0 1 .5-.5h.5a.5.5 0 0 1 .5-.5h.5a.5.5 0 0 1 .5-.5h.5a.5.5 0 0 1 .5-.5H11v-.5a.5.5 0 0 1 .5-.5h.5a.5.5 0 0 1 .5-.5H12V9h-1.37a.5.5 0 0 1-.37-.146z" />
                                                        </svg>
                                                        {t('edit_in_manual_mode') || 'Edit in Calculator'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-center justify-center gap-6">
                                            {/* Clickable Fullscreen Zoom Wrapper */}
                                            <div 
                                                onClick={() => {
                                                    if (!(device.isDesktop && enable3DLashingView && show3DLashingDiagram)) {
                                                        setIsFullscreenDiagramOpen(true);
                                                    }
                                                }}
                                                className={`relative w-full max-w-6xl mx-auto rounded-xl overflow-hidden border border-slate-200/50 dark:border-white/5 shadow-md bg-slate-50/50 dark:bg-[#0D111C]/60 transition-all duration-300 ${device.isDesktop && enable3DLashingView && show3DLashingDiagram
                                                    ? ''
                                                    : 'group cursor-pointer hover:border-orange-500/30'
                                                    }`}
                                            >
                                                {/* Zoom overlay badge */}
                                                {!(device.isDesktop && enable3DLashingView && show3DLashingDiagram) && (
                                                    <div className="absolute top-3 right-3 z-10 bg-slate-900/80 text-white rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 shadow-lg backdrop-blur-sm group-hover:bg-orange-600 transition-all select-none">
                                                        <Maximize2 className="w-3.5 h-3.5" />
                                                        <span className="text-[9px] font-bold tracking-wider uppercase">ZOOM / 3D</span>
                                                    </div>
                                                )}

                                                <LashingCard 
                                                    ref={lashingCardRef}
                                                    width={(transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (dimensions.width || 2.4) : (dimensions.length || 6)} 
                                                    depth={(transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (dimensions.length || 6) : (dimensions.width || 2.4)} 
                                                    height={dimensions.height || 2.6} 
                                                    weight={weight} 
                                                    cog={cog ? {
                                                        width: (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (cog.length || 0) : (cog.width || 0),
                                                        length: (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (cog.width || 0) : (cog.length || 0),
                                                        height: cog.height || 0
                                                    } : undefined}
                                                    lashingDetails={results?.lashing_details} 
                                                    className={`w-full h-[320px] sm:h-[400px] mx-auto transition-all duration-300 ${device.isDesktop && enable3DLashingView && show3DLashingDiagram ? 'pointer-events-auto' : 'pointer-events-none'} ${enable3DLashingView && show3DLashingDiagram ? 'opacity-100' : 'absolute -left-[9999px] opacity-0'}`}
                                                    theme={uiTheme} 
                                                    captureTheme="light" 
                                                    transportUnit={transportUnit} 
                                                    breakBulkOrientation={breakBulkOrientation}
                                                />
                                                {!(enable3DLashingView && show3DLashingDiagram) && (
                                                    results.lashing_diagram_png ? (
                                                        <div className="flex flex-col items-center justify-center p-2">
                                                            <img
                                                                key={`diagram-${results.lashing_diagram_png.substring(0, 32)}`}
                                                                src={`data:image/png;base64,${results.lashing_diagram_png}`}
                                                                alt="Lashing Diagram"
                                                                className="w-full h-[280px] sm:h-[400px] object-contain transition-colors"
                                                            />
                                                        </div>
                                                    ) : (<p className="text-slate-400 py-12">No diagram available</p>)
                                                )}
                                            </div>

                                            {/* Download PDF button (visible on mobile only) */}
                                            {!device.isDesktop && results.lashing_diagram_png && (
                                                <button onClick={(e) => { e.stopPropagation(); handlePDFDownloadClick(); }} className="w-full py-3 px-8 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white font-bold text-base rounded-xl shadow-lg shadow-slate-900/40 hover:shadow-xl hover:shadow-slate-900/50 transition-all duration-300 flex items-center justify-center gap-3">
                                                    Download PDF
                                                </button>
                                            )}

                                            {/* Breakbulk Flatrack Placement Diagram */}
                                            {transportUnit === 'breakbulk' && results.breakbulk_diagram_png && (
                                                <div className="w-full max-w-6xl mx-auto mt-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                                    <div className="px-3 py-2 border-b border-orange-200/50 dark:border-orange-500/20 bg-orange-50/30 dark:bg-orange-900/10 flex items-center gap-2 rounded-t-xl">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></div>
                                                        <h3 className="text-xs font-semibold text-orange-700 dark:text-orange-400/80">BREAKBULK — Flatrack Layout Plan</h3>
                                                        {results.breakbulk_layout && (
                                                            <div className="flex gap-2.5 text-[11px] border-l border-orange-200/50 dark:border-orange-500/20 pl-2.5">
                                                                <span className="font-medium text-orange-600/80 dark:text-orange-500/80 capitalize">
                                                                    ({results.breakbulk_layout.orientation})
                                                                </span>
                                                                <span className="font-normal text-slate-500 dark:text-orange-300/60">
                                                                    {results.breakbulk_layout.flatrack_type === 'flatrack_20' ? "20' Flatrack" : "40' Flatrack"}
                                                                </span>
                                                                <span className="text-slate-300 dark:text-slate-700">|</span>
                                                                <span className="font-medium text-orange-600/90 dark:text-orange-400/90">Left: {results.breakbulk_layout.left}</span>
                                                                <span className="text-slate-300 dark:text-slate-700">|</span>
                                                                <span className="font-medium text-blue-600/90 dark:text-blue-400/90">Right: {results.breakbulk_layout.right}</span>
                                                                <span className="text-slate-300 dark:text-slate-700">|</span>
                                                                <span className="text-slate-500/80 dark:text-slate-500/80">Total: {results.breakbulk_layout.user_count}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <img
                                                        src={`data:image/png;base64,${results.breakbulk_diagram_png}`}
                                                        alt="Breakbulk Flatrack Placement Diagram"
                                                        className="w-full max-h-[500px] object-contain rounded-b-xl bg-white/50 dark:bg-black/20 shadow-lg border border-orange-200/30 dark:border-orange-500/10"
                                                    />
                                                    {(results.breakbulk_layout?.warnings?.length ?? 0) > 0 && (
                                                        <div className="mt-2 flex flex-col gap-1">
                                                            {results.breakbulk_layout?.warnings?.map((w: string, i: number) => (
                                                                <p key={i} className="px-3 py-2 rounded-xl text-xs text-rose-700 dark:text-rose-400 font-semibold bg-rose-50 dark:bg-rose-900/20 border border-rose-300 dark:border-rose-600">⚠️ {w}</p>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Notes Section */}
                                            <div className="w-full max-w-5xl mt-2 px-1">
                                                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 gap-y-1">
                                                    <span className="font-bold text-xs md:text-sm inline-block mb-1 text-slate-700 dark:text-slate-300">{t('results.notes_title')}:</span> {t('results.notes_assumed')}
                                                    <br />• {t('results.notes_cargo_unitized')}
                                                    <br />• {t('results.notes_lashing_above_cog')}
                                                    <br />• {t('results.notes_angle_range')}
                                                    <br />• {t('results.notes_edge_protection')}
                                                    <br />• {t('results.notes_pretension_force')}
                                                    <br />• {t('results.notes_strength_limitations')}
                                                    <br />• {t('results.notes_distributed_uniformly')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
            {error && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="relative w-full max-w-sm sm:max-w-md bg-white dark:bg-[#0D111C] rounded-3xl p-6 shadow-2xl border border-red-200/60 dark:border-white/10 animate-in zoom-in-95 duration-200">
                        {/* Animated top accent bar */}
                        <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-3xl bg-gradient-to-r from-red-500 via-orange-500 to-red-500 animate-pulse" />

                        {/* (X) Close button */}
                        <button
                            onClick={() => { setError(null); setExceededDimsInfo(null); }}
                            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/20 transition-all flex items-center justify-center focus:outline-none"
                            aria-label="Close warning"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>

                        <div className="flex flex-col items-center text-center gap-3 pt-2">
                            {/* Animated warning icon */}
                            <div className="relative mt-2">
                                <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" style={{ animationDuration: '2s' }} />
                                <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-orange-500 shadow-xl shadow-red-500/30">
                                    <svg className="w-8 h-8 text-white drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Warning label */}
                            <span className="text-xs font-bold tracking-[0.25em] uppercase text-red-500 mt-1">WARNING</span>

                            {/* Error message */}
                            <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-slate-100 leading-relaxed whitespace-pre-line px-2">
                                {error}
                            </p>

                            {/* Visual dimension exceeded diagram */}
                            {exceededDimsInfo && selectedTransportUnitData && (
                                <div className="w-full mt-2 pt-3 border-t border-red-200/40 dark:border-red-500/10">
                                    <DimensionExceededDiagram
                                        cargoWidth={dimensions.width}
                                        cargoLength={dimensions.length}
                                        cargoHeight={dimensions.height}
                                        unitWidth={selectedTransportUnitData.width}
                                        unitLength={selectedTransportUnitData.length}
                                        unitHeight={selectedTransportUnitData.height}
                                        unitName={exceededDimsInfo.unitName}
                                        exceededDims={exceededDimsInfo.dims}
                                    />
                                </div>
                            )}

                            {/* Bottom confirm/close button */}
                            <button
                                onClick={() => { setError(null); setExceededDimsInfo(null); }}
                                className="w-full mt-3 py-3 px-5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold text-sm rounded-2xl shadow-lg shadow-red-500/25 active:scale-95 transition-all"
                            >
                                Tamam / Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ProjectModal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} onConfirm={(desc) => downloadProfessionalPDF(desc)} initialDescription={projectDescription} isGenerating={isGeneratingPDF} />

            {isFullscreenDiagramOpen && results && (
                <div className="fixed inset-0 z-[120] bg-slate-950/95 backdrop-blur-md flex flex-col justify-between p-4 md:p-6 animate-in fade-in duration-200">
                    {/* Header with Title and Close Button */}
                    <div className="flex items-center justify-between w-full pb-3 border-b border-white/10">
                        <h3 className="text-white font-bold text-base sm:text-lg">{t('lashing_diagram')}</h3>
                        <button
                            type="button"
                            onClick={() => setIsFullscreenDiagramOpen(false)}
                            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all focus:outline-none"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    {/* Content Container */}
                    <div className="flex-1 flex items-center justify-center w-full overflow-hidden p-2">
                        {enable3DLashingView && show3DLashingDiagram ? (
                            <div className="w-full h-full max-w-5xl">
                                <LashingCard 
                                    width={(transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (dimensions.width || 2.4) : (dimensions.length || 6)} 
                                    depth={(transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (dimensions.length || 6) : (dimensions.width || 2.4)} 
                                    height={dimensions.height || 2.6} 
                                    weight={weight} 
                                    cog={cog ? {
                                        width: (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (cog.length || 0) : (cog.width || 0),
                                        length: (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') ? (cog.width || 0) : (cog.length || 0),
                                        height: cog.height || 0
                                    } : undefined}
                                    lashingDetails={results?.lashing_details} 
                                    className="w-full h-full" 
                                    theme={uiTheme} 
                                    captureTheme="light" 
                                    transportUnit={transportUnit} 
                                    breakBulkOrientation={breakBulkOrientation}
                                />
                            </div>
                        ) : (
                            results.lashing_diagram_png ? (
                                <img
                                    src={`data:image/png;base64,${results.lashing_diagram_png}`}
                                    alt="Lashing Diagram"
                                    className="w-full h-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                                />
                            ) : (
                                <p className="text-slate-400">No diagram available</p>
                            )
                        )}
                    </div>

                    {/* Tip text at the bottom */}
                    <div className="text-center text-xs text-slate-400 font-medium pb-2 select-none">
                        {t('results.fullscreen_zoom_tip') || 'Ekranı yatay çevirerek daha geniş inceleyebilirsiniz.'}
                    </div>
                </div>
            )}

            <PdfViewerModal
                isOpen={isPdfViewerModalOpen}
                onClose={() => setIsPdfViewerModalOpen(false)}
                report={selectedPdfReport}
            />

            <SavedPdfReportsModal
                isOpen={isSavedPdfModalOpen}
                onClose={() => setIsSavedPdfModalOpen(false)}
                onSelectReport={(report) => {
                    setSelectedPdfReport(report);
                    setIsSavedPdfModalOpen(false);
                    setIsPdfViewerModalOpen(true);
                }}
            />

            <RecentCalculationsModal
                isOpen={isRecentCalculationsModalOpen}
                onClose={() => setIsRecentCalculationsModalOpen(false)}
            />

            <ConfirmationModal
                isOpen={showFlatrackWarning}
                title={t('common.confirm')}
                message={t('warnings.flatrack_dunnage_required')}
                onConfirm={() => {
                    setFlatrackWarningPending(true);
                    setShowFlatrackWarning(false);
                }}
                onCancel={() => {
                    setShowFlatrackWarning(false);
                    setFlatrackWarningPending(false);
                    // Auto-enable wooden dunnage when user clicks "No"
                    setWoodenDunnageEnabled(true);
                    setWoodenDunnageData(prev => ({
                        ...prev,
                        width: 10,
                        length: 10,
                        forces: {
                            ...prev.forces,
                            forward: 2,
                            backward: 2
                        }
                    }));
                }}
            />



            <MobileNavBar
                activeTab={mobileTab}
                setActiveTab={setMobileTab}
                onCalculate={handleCalculate}
                isCalculating={isCalculating}
                hasResults={!!results}
                t={t}
            />

            <footer className="layout-footer bg-white dark:bg-[#0D111C]/90 border-t border-slate-200 dark:border-white/10 dark:backdrop-blur-xl transition-all duration-300 py-2 lg:py-4">
                <div className="container-section">
                    {/* Ultra-thin Mobile Footer (< 768px) */}
                    <div className="md:hidden flex flex-col items-center justify-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                        <div className="flex items-center gap-2 font-medium">
                            <span>Lashing Calculator v2.4.2</span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                Online
                            </span>
                        </div>
                        <div className="text-[9px] text-slate-400 dark:text-slate-500 text-center leading-tight">
                            <CalculationWarningBanner />
                        </div>
                    </div>

                    {/* Desktop Footer (>= 768px) - Untouched */}
                    <div className="hidden md:flex flex-col items-center gap-3">
                        <div className="flex flex-row items-center justify-between w-full gap-4">
                            <div className="flex items-center gap-3">
                                <img src="/resized-buckles-son.png" alt="Lashing Calculator Logo" className="w-8 h-8 object-contain opacity-80" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <div className="font-semibold text-gray-800 dark:text-gray-200">Lashing Calculator</div>
                                    <div className="text-xs">Professional Cargo Securing Solutions</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400"><span>v2.4.2</span><span>•</span><span>© 2025 Professional Engineering Tools</span></div>
                            <div className="flex items-center gap-4"><div className="flex items-center gap-2 text-xs text-gray-500"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div><span>System Online</span></div></div>
                        </div>
                        <div className="text-center text-xs text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-200 dark:border-white/5 w-full">
                            <CalculationWarningBanner />
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Calculator;
